import { z } from 'zod';

import { WALLET_MIN_RECHARGE_AMOUNT_INR } from './pricing';
import { PWA_CONTACT_SHARE_PATH } from './pwa';
import {
  activitySources,
  activityTypes,
  broadcastRecipientStatuses,
  conversationChannels,
  conversationMessageTypes,
  debitTypes,
  insertActivitySchema,
  insertContactMessageSchema,
  insertLeadCategorySchema,
  insertLeadSchema,
  insertProductSchema,
  insertProductUpdateSchema,
  insertUserSchema,
  LEAD_FOLLOWUP_DEFAULT_DURATION_MINUTES,
  LEAD_FOLLOWUP_MAX_DURATION_MINUTES,
  LEAD_FOLLOWUP_MIN_DURATION_MINUTES,
  leadConversionStatuses,
  leadFollowupPurposes,
  leadPipelineStages,
  leadStatuses,
  messageDeliveryStatuses,
  localeSchema,
  notificationTypes,
  productAvailabilityStatuses,
  productPriceModes,
  productStatuses,
  productTypes,
  normalizeProductInventory,
  validateProductInventory,
  publicSignupSchema,
  serviceKeys,
  sourceTypes,
  subscriptionStatuses,
  transactionTypes,
  userCapabilities,
  userPreferencesSchema,
  userRoles,
} from './schema';
import { socialAssistantSettingsSchema, socialAssistantStateSchema } from './socialAssistant';
import {
  PRODUCT_IMAGE_DATA_URL_MAX_LENGTH,
  PRODUCT_VIDEO_DATA_URL_MAX_LENGTH,
  productVideoMimeTypes,
  SETTINGS_IMAGE_DATA_URL_MAX_LENGTH,
  SOCIAL_POST_VIDEO_DATA_URL_MAX_LENGTH,
} from './upload';
import {
  getWhatsAppMediaMaxBytes,
  WHATSAPP_MEDIA_CAPTION_MAX_LENGTH,
  whatsappMediaMimeTypes,
  whatsappStoredMediaMimeTypes,
} from './whatsappMedia';
import {
  templateKeySchema,
  templateNameSchema,
  templateVariableDefinitionSchema,
  whatsAppTemplateComponentSchema,
  whatsAppTemplateDetailSchema,
  whatsAppTemplateDraftSchema,
  whatsAppTemplateMediaHeaderFormatOptions,
  whatsAppTemplateMessageSchema,
  whatsAppTemplatePreviewSchema,
  whatsAppTemplateSampleMediaSchema,
  whatsAppTemplateSummarySchema,
  whatsAppTemplateValidationSchema,
  withBroadcastUnsubscribeFooter,
} from './whatsappTemplates';

export const apiErrorResponseSchema = z.object({
  message: z.string().min(1),
  code: z.string().min(1),
});

export type ApiErrorResponse = z.infer<typeof apiErrorResponseSchema>;

export const errorSchemas = {
  validation: apiErrorResponseSchema,
  notFound: apiErrorResponseSchema,
  internal: apiErrorResponseSchema,
  unauthorized: apiErrorResponseSchema,
};

const trimStringInput = (value: unknown) => (typeof value === 'string' ? value.trim() : value);

const trimmedTextSchema = (maxLength: number) =>
  z.preprocess(trimStringInput, z.string().max(maxLength).default(''));

const gstinSchema = z.preprocess(
  value => (typeof value === 'string' ? value.trim().toUpperCase() : value),
  z
    .string()
    .regex(/^(?:|[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z])$/, 'Enter a valid GSTIN.')
    .default('')
);

const gstStateCodeSchema = z.preprocess(
  trimStringInput,
  z
    .string()
    .regex(/^(?:|[0-9]{2})$/, 'Enter a two-digit GST state code.')
    .default('')
);

const stringListSchema = (maxItems = 50, maxLength = 120) =>
  z
    .array(z.preprocess(trimStringInput, z.string().min(1).max(maxLength)))
    .max(maxItems)
    .default([]);

const weekDaySchema = z.enum(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']);

const weekDayListSchema = z.array(weekDaySchema).max(7).default([]);

export const aiToneSchema = z.enum(['professional', 'friendly', 'closing', 'urgent']);

export const companyDetailsSettingsSchema = z
  .object({
    businessDescription: trimmedTextSchema(4000),
    businessAddress: trimmedTextSchema(500),
    city: trimmedTextSchema(120),
    state: trimmedTextSchema(120),
    gstStateCode: gstStateCodeSchema,
    billingPostalCode: trimmedTextSchema(20),
    gstin: gstinSchema,
    country: trimmedTextSchema(120),
    timeZone: trimmedTextSchema(64),
    supportedServiceAreas: stringListSchema(50, 120),
    businessHours: trimmedTextSchema(300),
    weeklyWorkingDays: weekDayListSchema,
    holidayOrOutOfHoursBehavior: trimmedTextSchema(2000),
    productsOffered: stringListSchema(100, 160),
    servicesOffered: stringListSchema(100, 160),
    productServiceDescriptions: trimmedTextSchema(4000),
    typicalPriceRange: trimmedTextSchema(160),
    supportedBrandsOrCategories: stringListSchema(100, 160),
    availableLocationsBranches: stringListSchema(100, 160),
    currentOffersOrPromotions: trimmedTextSchema(2000),
    faqs: stringListSchema(50, 240),
    neverClaimWithoutConfirmation: trimmedTextSchema(2000),
    targetCustomerDescription: trimmedTextSchema(2000),
    defaultSalesProcess: trimmedTextSchema(2000),
    qualificationQuestions: stringListSchema(50, 240),
    requiredLeadInformation: stringListSchema(50, 160),
    leadGoals: stringListSchema(25, 80),
    escalationConditions: trimmedTextSchema(2000),
    humanSupportContactDetails: trimmedTextSchema(1200),
    preferredReplyLength: z.enum(['short', 'medium', 'long']).default('medium'),
    preferredCommunicationTone: z
      .enum(['professional', 'friendly', 'consultative', 'concise'])
      .default('professional'),
    supportedLanguages: stringListSchema(20, 80),
    defaultFallbackLanguage: trimmedTextSchema(40),
    customAiInstructions: trimmedTextSchema(4000),
    restrictedTopics: stringListSchema(50, 120),
    avoidWordsOrClaims: stringListSchema(50, 120),
  })
  .superRefine((value, ctx) => {
    if (value.gstin && value.gstStateCode && !value.gstin.startsWith(value.gstStateCode)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['gstStateCode'],
        message: 'GST state code must match the first two digits of the GSTIN.',
      });
    }
  });

export const whatsappAiAutomationSettingsSchema = z
  .object({
    whatsappAiEnabled: z.boolean().default(false),
    defaultReplyLanguage: z.enum(['english', 'hindi', 'hinglish']).default('english'),
    aiModel: trimmedTextSchema(120),
    replyDelayMinSeconds: z.preprocess(
      value => (value === '' || value == null ? undefined : value),
      z.coerce.number().int().min(0).max(3600).default(0)
    ),
    replyDelayMaxSeconds: z.preprocess(
      value => (value === '' || value == null ? undefined : value),
      z.coerce.number().int().min(0).max(3600).default(5)
    ),
    maxConversationHistoryMessages: z.preprocess(
      value => (value === '' || value == null ? undefined : value),
      z.coerce.number().int().min(1).max(100).default(12)
    ),
    maxContextTokenBudget: z.preprocess(
      value => (value === '' || value == null ? undefined : value),
      z.coerce.number().int().min(2048).max(8192).default(8000)
    ),
    maxRepliesPerConversationWindow: z.preprocess(
      value => (value === '' || value == null ? undefined : value),
      z.coerce.number().int().min(1).max(50).default(10)
    ),
    autoResponseDuringBusinessHours: z.boolean().default(true),
    autoResponseOutsideBusinessHours: z.boolean().default(false),
    outOfHoursInstructions: trimmedTextSchema(4000),
    inactivityResumeHours: z.preprocess(
      value => (value === '' || value == null ? undefined : value),
      z.coerce.number().int().min(1).max(168).default(2)
    ),
    confidenceThreshold: z.preprocess(
      value => (value === '' || value == null ? undefined : value),
      z.coerce.number().min(0).max(100).default(60)
    ),
  })
  .superRefine((value, ctx) => {
    if (value.replyDelayMaxSeconds < value.replyDelayMinSeconds) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['replyDelayMaxSeconds'],
        message: 'Maximum reply delay must be at least the minimum reply delay.',
      });
    }
  });

/** Generated captions end with at most this many hashtags. */
export const SOCIAL_CAPTION_MAX_HASHTAGS = 5;
/** Every generated image and video is a vertical 9:16 creative. */
export const SOCIAL_MEDIA_ASPECT_RATIO = '9:16';
/** One Veo generation; longer clips would need paid continuations, which are not offered. */
export const SOCIAL_VIDEO_DURATION_SECONDS = 8;

export const socialAutomationSettingsSchema = z
  .object({
    enabled: z.boolean().default(false),
    autoApprove: z.boolean().default(false),
    platforms: z
      .array(z.enum(['facebook', 'instagram', 'threads']))
      .max(3)
      .default([]),
    postingFrequency: z.enum(['daily', 'every_2_days', 'weekly']).default('daily'),
    weeklyDay: weekDaySchema.default('mon'),
    preferredPostingTime: z
      .string()
      .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Posting time must use HH:mm format.')
      .default('10:00'),
    contentTone: z
      .enum(['professional', 'friendly', 'luxury', 'festive', 'sales_focused'])
      .default('professional'),
    captionLanguage: z.enum(['auto', 'english', 'hindi', 'hinglish']).default('auto'),
    // Settings saved before the image/video split stored `any`; those tenants now get images.
    postType: z.preprocess(
      value => (value === 'any' ? 'image' : value),
      z.enum(['image', 'video']).default('image')
    ),
    // Free-text direction sent with every image or video request for this workspace.
    imageInstructions: trimmedTextSchema(4000),
    videoInstructions: trimmedTextSchema(4000),
  })
  .superRefine((value, ctx) => {
    if (value.enabled && value.platforms.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['platforms'],
        message: 'Select at least one platform before enabling automation.',
      });
    }
  });

export type CompanyDetailsSettings = z.infer<typeof companyDetailsSettingsSchema>;
export type WhatsAppAiAutomationSettings = z.infer<typeof whatsappAiAutomationSettingsSchema>;
export type SocialAutomationSettings = z.infer<typeof socialAutomationSettingsSchema>;

export const aiSalesAssistantSuggestionSchema = z.object({
  leadIntent: z.string(),
  engagementLevel: z.enum(['LOW', 'MEDIUM', 'HIGH']),
  leadTemperature: z.enum(['COLD', 'WARM', 'HOT']),
  urgencyLevel: z.enum(['LOW', 'MEDIUM', 'HIGH']),
  conversionProbability: z.number().min(0).max(100),
  salesStrategy: z.string(),
  reasoning: z.string(),
  nextBestAction: z.object({
    action: z.string(),
    why: z.string(),
    recommendedWithin: z.string(),
  }),
  whatsappDraft: z.object({
    message: z.string(),
    bestTimeToSend: z.string(),
  }),
  emailDraft: z.object({
    subject: z.string(),
    body: z.string(),
  }),
  followUpPlan: z.object({
    nextFollowUpAt: z.string(),
    channel: z.enum(['CALL', 'WHATSAPP', 'EMAIL', 'MEETING']),
    goal: z.string(),
  }),
  objectionHandling: z.array(
    z.object({
      objection: z.string(),
      recommendedResponse: z.string(),
    })
  ),
  recommendedQuestions: z.array(z.string()),
  riskFactors: z.array(z.string()),
  opportunities: z.array(z.string()),
});

export const aiSalesAssistantResponseSchema = z.object({
  suggestions: aiSalesAssistantSuggestionSchema,
  meta: z.object({
    leadId: z.number(),
    sourceVersion: z.string(),
    generatedAt: z.string(),
    providerRequestCount: z.number().int().min(0),
    cached: z.boolean().optional(),
    fallbackReason: z.string().optional(),
  }),
});

export type AiTone = z.infer<typeof aiToneSchema>;
export type AiSalesAssistantSuggestion = z.infer<typeof aiSalesAssistantSuggestionSchema>;
export type AiSalesAssistantResponse = z.infer<typeof aiSalesAssistantResponseSchema>;

const whatsAppTemplateMutationResponseSchema = z.object({
  message: z.string(),
  template: whatsAppTemplateDetailSchema,
});

const whatsAppTemplateDeleteScopeSchema = z.enum(['local', 'meta', 'both']);

const whatsAppTemplateDeleteResponseSchema = z.object({
  message: z.string(),
  deletedScope: whatsAppTemplateDeleteScopeSchema,
  template: whatsAppTemplateDetailSchema.nullable(),
});

const whatsAppTemplateSyncResponseSchema = z.object({
  message: z.string(),
  syncedCount: z.number(),
  templates: z.array(whatsAppTemplateSummarySchema),
});

const passwordResetRequestResponseSchema = z.object({
  message: z.string(),
  deliveryChannel: z.enum(['whatsapp', 'email', 'none']).optional(),
  fallbackUsed: z.boolean().optional(),
  cooldownSeconds: z.number().int().positive().optional(),
});

const passwordResetVerificationResponseSchema = z.object({
  message: z.string(),
  resetToken: z.string(),
  expiresAt: z.string(),
});

const omitInvalidOptionalQueryValue = (value: unknown) =>
  typeof value === 'string' && ['', 'undefined', 'null', 'NaN'].includes(value) ? undefined : value;

const optionalQueryStringSchema = z.preprocess(
  omitInvalidOptionalQueryValue,
  z.string().optional()
);
const optionalQueryNumberSchema = z.preprocess(
  omitInvalidOptionalQueryValue,
  z.coerce.number().optional()
);
const optionalQueryDateSchema = z.preprocess(
  omitInvalidOptionalQueryValue,
  z.string().date().optional()
);

const optionalQueryDateTimeSchema = z.preprocess(
  omitInvalidOptionalQueryValue,
  z.union([z.string().date(), z.string().datetime({ offset: true })]).optional()
);

const reportFiltersSchema = z.object({
  preset: z
    .enum(['today', 'yesterday', 'this_week', 'last_week', 'this_month', 'last_month', 'custom'])
    .optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  managerId: z.coerce.number().optional(),
  salesmanId: z.coerce.number().optional(),
  leadSource: optionalQueryStringSchema,
  source: optionalQueryStringSchema,
  productId: z.coerce.number().optional(),
  leadStatus: z.string().optional(),
  assignedUserId: z.coerce.number().optional(),
  createdUserId: z.coerce.number().optional(),
  followupState: z.enum(['pending', 'overdue']).optional(),
  conversionStatus: z.enum(leadConversionStatuses).optional(),
  minValue: optionalQueryNumberSchema,
  maxValue: optionalQueryNumberSchema,
  createdFrom: optionalQueryDateSchema,
  createdTo: optionalQueryDateSchema,
  followupFrom: optionalQueryDateSchema,
  followupTo: optionalQueryDateSchema,
  city: z.string().optional(),
  search: z.string().optional(),
  page: z.coerce.number().optional(),
  limit: z.coerce.number().optional(),
  unassignedPage: z.coerce.number().optional(),
  unassignedLimit: z.coerce.number().optional(),
});

const paginationQuerySchema = z.object({
  page: z.coerce.number().optional(),
  limit: z.coerce.number().optional(),
});

const serviceKeySchema = z.enum(serviceKeys);
const walletTransactionTypeSchema = z.enum(transactionTypes);
const walletDebitTypeSchema = z.enum(debitTypes);
const walletSourceTypeSchema = z.enum(sourceTypes);

const userListStatusSchema = z.enum(['active', 'terminated', 'all']);

const paginatedResponseSchema = <T extends z.ZodTypeAny>(itemSchema: T) =>
  z.object({
    items: z.array(itemSchema),
    page: z.number(),
    limit: z.number(),
    total: z.number(),
    pageCount: z.number(),
  });

const leadTodayFollowupStatusSchema = z.enum(['pending', 'completed']);
const leadFollowupPurposeSchema = z.enum(leadFollowupPurposes);
const leadPipelineStageSchema = z.enum(leadPipelineStages);

const followupAvailabilityInputSchema = z.object({
  startAt: z.coerce.date(),
  durationMinutes: z.coerce
    .number()
    .int()
    .min(LEAD_FOLLOWUP_MIN_DURATION_MINUTES)
    .max(LEAD_FOLLOWUP_MAX_DURATION_MINUTES)
    .default(LEAD_FOLLOWUP_DEFAULT_DURATION_MINUTES),
  excludeLeadId: z.coerce.number().optional(),
});

const followupCalendarEventSchema = z.object({
  id: z.string(),
  source: z.enum(['lead', 'activity']),
  title: z.string(),
  startsAt: z.string(),
  endsAt: z.string(),
  leadId: z.number().nullable(),
  leadName: z.string().nullable(),
  purpose: leadFollowupPurposeSchema.nullable(),
  type: z.string().nullable(),
  ownerName: z.string().nullable(),
});

const followupAvailabilityResponseSchema = z.object({
  isAvailable: z.boolean(),
  windowStart: z.string(),
  windowEnd: z.string(),
  durationMinutes: z.number(),
  conflicts: z.array(followupCalendarEventSchema),
  events: z.array(followupCalendarEventSchema),
  suggestions: z.array(z.string()),
});

const dateLikeSchema = z.union([z.string(), z.date()]).nullable();
const optionalDateLikeSchema = z.union([z.string(), z.date()]).nullable().optional();

export const minimalUserPreviewSchema = z.object({
  id: z.number(),
  name: z.string(),
});

export const authUserSchema = minimalUserPreviewSchema.extend({
  username: z.string(),
  email: z.string(),
  phone: z.string(),
  preferredLocale: z.string().nullable(),
  timeZone: z.string().nullable(),
  role: z.enum(userRoles),
  roles: z.array(z.enum(userCapabilities)).nullable(),
  adminId: z.number().nullable(),
  managerId: z.number().nullable(),
});

export const userListItemSchema = authUserSchema.extend({
  isTerminated: z.boolean(),
  manager: minimalUserPreviewSchema.nullable().optional(),
});

export const teamMemberSchema = minimalUserPreviewSchema.extend({
  username: z.string(),
  email: z.string(),
  phone: z.string(),
  role: z.enum(userRoles),
  createdAt: optionalDateLikeSchema,
});

export const superAdminUserListItemSchema = minimalUserPreviewSchema.extend({
  username: z.string(),
  email: z.string(),
  role: z.enum(userRoles),
  subscriptionStatus: z.enum(subscriptionStatuses).nullable(),
  subscriptionStartDate: optionalDateLikeSchema,
  subscriptionExpiryDate: optionalDateLikeSchema,
  daysRemaining: z.number().nullable(),
});

const superAdminSubscriberStatsItemSchema = superAdminUserListItemSchema.extend({
  totalLeads: z.number(),
  createdLeads: z.number(),
  assignedLeads: z.number(),
  openLeads: z.number(),
  closedWonLeads: z.number(),
  closedLostLeads: z.number(),
  pendingFollowups: z.number(),
  overdueFollowups: z.number(),
  wonRevenue: z.number(),
  conversionRate: z.number(),
  lastLeadCreatedAt: optionalDateLikeSchema,
  daysSinceLastLead: z.number().nullable(),
});

const superAdminSubscriberStatsResponseSchema = z.object({
  summary: z.object({
    totalSubscribers: z.number(),
    totalLeads: z.number(),
    createdLeads: z.number(),
    openLeads: z.number(),
    closedWonLeads: z.number(),
    closedLostLeads: z.number(),
    pendingFollowups: z.number(),
    overdueFollowups: z.number(),
    wonRevenue: z.number(),
  }),
  items: z.array(superAdminSubscriberStatsItemSchema),
});

export const couponSchema = z.object({
  id: z.number(),
  code: z.string(),
  discountType: z.enum(['percentage', 'fixed']),
  discountValue: z.union([z.string(), z.number()]),
  maxUses: z.number().nullable().optional(),
  usedCount: z.number(),
  minOrderAmount: z.number(),
  expiresAt: optionalDateLikeSchema,
  isActive: z.boolean(),
  createdBy: z.number().nullable().optional(),
  createdAt: optionalDateLikeSchema,
  updatedAt: optionalDateLikeSchema,
});

const subscriptionInvoiceResponseSchema = z.object({
  invoiceNumber: z.string(),
  invoiceDate: z.string(),
  paymentDate: z.string(),
  planName: z.string(),
  billedTo: z.object({
    name: z.string(),
    email: z.string(),
    phone: z.string().nullable(),
    address: z.string(),
    city: z.string(),
    state: z.string(),
    stateCode: z.string(),
    postalCode: z.string(),
    country: z.string(),
    gstin: z.string().nullable(),
  }),
  issuedBy: z.object({
    name: z.string(),
    email: z.string(),
    address: z.string(),
    gstin: z.string(),
    state: z.string(),
    stateCode: z.string(),
    sacCode: z.string(),
  }),
  subscription: z.object({
    status: z.string(),
    startDate: z.string().nullable(),
    expiryDate: z.string().nullable(),
  }),
  pricing: z.object({
    currency: z.literal('INR'),
    listPrice: z.number(),
    discountAmount: z.number(),
    couponCode: z.string().nullable(),
    taxableAmount: z.number(),
    gstRatePercent: z.number(),
    gstAmount: z.number(),
    cgstAmount: z.number(),
    sgstAmount: z.number(),
    igstAmount: z.number(),
    totalPaid: z.number(),
  }),
  placeOfSupply: z.object({
    state: z.string(),
    stateCode: z.string(),
    isInterState: z.boolean(),
  }),
  transaction: z.object({
    paymentId: z.string().nullable(),
    orderId: z.string(),
    status: z.literal('PAID'),
  }),
});

export const productPreviewSchema = z.object({
  id: z.number(),
  name: z.string(),
  archivedAt: z.string().nullable().optional(),
});

export const leadCategoryPreviewSchema = z.object({
  id: z.number(),
  name: z.string(),
});

export const leadCategoryListItemSchema = leadCategoryPreviewSchema.extend({
  isDefault: z.boolean(),
});

export const productMediaItemSchema = z.object({
  id: z.number(),
  url: z.string(),
  fileName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number(),
  altText: z.string().nullable(),
  sortOrder: z.number(),
});

export const productListItemSchema = productPreviewSchema.extend({
  archivedBy: z.number().nullable(),
  linkedLeadCount: z.number().int().nonnegative().optional(),
  description: z.string().nullable(),
  price: z.number().nullable(),
  priceMode: z.enum(productPriceModes),
  type: z.enum(productTypes),
  status: z.enum(productStatuses),
  brand: z.string().nullable(),
  category: z.string().nullable(),
  currency: z.string(),
  salePrice: z.number().nullable(),
  negotiationMinPrice: z.number().nullable(),
  negotiationMaxPrice: z.number().nullable(),
  availability: z.enum(productAvailabilityStatuses),
  inventoryQuantity: z.number().nullable(),
  productUrl: z.string().nullable(),
  parentProductId: z.number().nullable(),
  media: z.array(productMediaItemSchema),
});

const httpsUrlSchema = z
  .string()
  .trim()
  .url()
  .max(2048)
  .refine(value => new URL(value).protocol === 'https:', 'URL must use HTTPS.');

const productSyncImageSchema = z.object({
  // A stable source image ID is strongly recommended so a changed URL replaces that one image.
  id: z.string().trim().min(1).max(191).optional(),
  url: httpsUrlSchema,
  altText: z.string().trim().max(500).optional(),
});

const productSyncUpsertSchema = z
  .object({
    operation: z.literal('upsert'),
    productId: z.string().trim().min(1).max(191),
    name: z.string().trim().min(1).max(500),
    description: z.string().trim().max(10000).nullable().optional(),
    priceInPaise: z.number().int().min(1).max(2_147_483_647),
    type: z.enum(productTypes).default('product'),
    status: z.enum(productStatuses).default('active'),
    priceMode: z.enum(productPriceModes).default('fixed'),
    brand: z.string().trim().max(160).nullable().optional(),
    category: z.string().trim().max(160).nullable().optional(),
    currency: z
      .string()
      .trim()
      .toUpperCase()
      .regex(/^[A-Z]{3}$/)
      .default('INR'),
    salePriceInPaise: z.number().int().min(0).max(2_147_483_647).nullable().optional(),
    availability: z.enum(productAvailabilityStatuses).default('in_stock'),
    inventoryQuantity: z.number().int().min(0).max(2_147_483_647).nullable().optional(),
    productUrl: httpsUrlSchema.nullable().optional(),
    // Omit to leave integration-managed images unchanged; [] removes all of them.
    images: z.array(productSyncImageSchema).max(10).optional(),
  })
  .transform(normalizeProductInventory)
  .superRefine(validateProductInventory)
  .superRefine((value, context) => {
    if (value.salePriceInPaise != null && value.priceMode === 'dynamic') {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['salePriceInPaise'],
        message: 'Dynamic-price products cannot include a sale price.',
      });
    }
    if (value.salePriceInPaise != null && value.salePriceInPaise > value.priceInPaise) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['salePriceInPaise'],
        message: 'Sale price cannot exceed the base price.',
      });
    }
  });

export const productSyncWebhookSchema = z.union([
  productSyncUpsertSchema,
  z.object({
    operation: z.literal('delete'),
    productId: z.string().trim().min(1).max(191),
  }),
]);

const productIntegrationKeyStatusSchema = z.object({
  configured: z.boolean(),
  keyPrefix: z.string().nullable(),
  createdAt: z.string().nullable(),
  lastUsedAt: z.string().nullable(),
});

export const productCatalogSyncResultSchema = z.object({
  catalogId: z.string(),
  catalogCreated: z.boolean(),
  submittedCount: z.number(),
  deletedCount: z.number(),
  skippedCount: z.number(),
  handles: z.array(z.string()),
  warningCount: z.number(),
  errorCount: z.number(),
});

export const productMetaCatalogStatusSchema = z.object({
  isFacebookConnected: z.boolean(),
  catalogAvailable: z.boolean(),
  canCreateCatalog: z.boolean(),
  catalogId: z.string().nullable(),
  catalogName: z.string().nullable(),
  catalogs: z.array(
    z.object({
      id: z.string(),
      name: z.string().nullable(),
    })
  ),
  businesses: z.array(
    z.object({
      id: z.string(),
      name: z.string().nullable(),
    })
  ),
});

export const productMetaCatalogCreateInputSchema = z.object({
  businessId: z.string().trim().min(1).max(191),
  name: z.string().trim().min(1).max(140),
});

export const productMetaCatalogCreateResultSchema = z.object({
  id: z.string(),
  name: z.string(),
});

export const productMetaCatalogDeleteResultSchema = z.void();

export const productCatalogSyncInputSchema = z
  .object({
    createCatalogIfMissing: z.boolean().optional().default(false),
  })
  .default({});

export const productMetaCatalogLinkInputSchema = z.object({
  catalogId: z.string().trim().min(1).max(191),
  whatsappAccountIds: z.array(z.number().int().positive()).min(1).max(25),
});

export const productMetaCatalogLinkResultSchema = z.object({
  catalogId: z.string(),
  linkedWhatsAppAccountIds: z.array(z.number().int().positive()),
});

export const leadProductSnapshotSchema = z.object({
  name: z.string(),
  sku: z.string().nullable(),
  variant: z.string().nullable(),
  price: z.number(),
  currency: z.string(),
});

export const leadListItemSchema = z.object({
  id: z.number(),
  leadCode: z.string(),
  name: z.string(),
  company: z.string().nullable(),
  email: z.string(),
  phone: z.string(),
  productId: z.number().nullable(),
  productSnapshot: leadProductSnapshotSchema.nullable().optional(),
  categoryId: z.number().nullable(),
  value: z.number().nullable(),
  createdAt: optionalDateLikeSchema,
  nextFollowupDate: dateLikeSchema,
  nextFollowupPurpose: leadFollowupPurposeSchema.nullable(),
  nextFollowupDurationMinutes: z.number().nullable(),
  status: z.enum(leadStatuses),
  pipelineStage: leadPipelineStageSchema,
  source: z.string(),
  assignedUserId: z.number().nullable(),
  assignedUser: minimalUserPreviewSchema.nullable(),
  createdByUser: minimalUserPreviewSchema.nullable(),
  product: productPreviewSchema.nullable(),
  category: leadCategoryPreviewSchema.nullable(),
  todayFollowupStatus: leadTodayFollowupStatusSchema.nullable().optional(),
});

export const leadDetailSchema = leadListItemSchema.extend({
  address: z.string().nullable(),
  notes: z.string().nullable(),
  source: z.string(),
  managerName: z.string().nullable(),
  notificationEvents: z
    .array(
      z.object({
        id: z.number(),
        eventKey: z.string(),
        recipient: z.string().nullable(),
        sentAt: z.string(),
      })
    )
    .optional(),
});

const leadKanbanColumnSchema = z.object({
  items: z.array(leadListItemSchema),
  page: z.number(),
  limit: z.number(),
  total: z.number(),
  hasMore: z.boolean(),
});

const leadKanbanColumnsSchema = z.object({
  LEAD_CREATED: leadKanbanColumnSchema,
  CONTACTED: leadKanbanColumnSchema,
  FOLLOWUP_SCHEDULED: leadKanbanColumnSchema,
  QUOTATION_SENT: leadKanbanColumnSchema,
  CONVERSION: leadKanbanColumnSchema,
  LOST: leadKanbanColumnSchema,
});

const leadKanbanResponseSchema = z.object({
  columns: leadKanbanColumnsSchema,
});

const leadKanbanStageResponseSchema = leadKanbanColumnSchema.extend({
  stage: leadPipelineStageSchema,
});

const leadPipelineStageUpdateInputSchema = z.object({
  pipelineStage: leadPipelineStageSchema,
  reason: z.string().trim().max(500).optional(),
});

export const leadAssignmentHistoryItemSchema = z.object({
  id: z.number(),
  leadId: z.number(),
  previousAssignedUserId: z.number().nullable(),
  newAssignedUserId: z.number().nullable(),
  changedBy: z.number().nullable(),
  reason: z.string().nullable(),
  changedAt: optionalDateLikeSchema,
  previousAssignedUser: minimalUserPreviewSchema.nullable(),
  newAssignedUser: minimalUserPreviewSchema.nullable(),
  changedByUser: minimalUserPreviewSchema.nullable(),
});

export const activityListLeadSchema = z.object({
  id: z.number(),
  leadCode: z.string(),
  name: z.string(),
  company: z.string().nullable(),
  email: z.string(),
  phone: z.string(),
  productId: z.number().nullable(),
  productSnapshot: leadProductSnapshotSchema.nullable().optional(),
  value: z.number().nullable(),
  status: z.enum(leadStatuses),
  assignedUserId: z.number().nullable(),
  assignedUser: minimalUserPreviewSchema.nullable(),
  product: productPreviewSchema.nullable(),
});

export const activityListItemSchema = z.object({
  id: z.number(),
  leadId: z.number(),
  userId: z.number(),
  type: z.enum(activityTypes),
  content: z.string(),
  scheduledAt: dateLikeSchema,
  isCompleted: z.boolean(),
  createdAt: optionalDateLikeSchema,
  user: minimalUserPreviewSchema.nullable(),
  lead: activityListLeadSchema.nullable(),
  source: z.enum(activitySources).nullable().optional(),
  media: z
    .object({
      url: z.string(),
      mimeType: z.enum(whatsappMediaMimeTypes),
      fileName: z.string(),
      sizeBytes: z.number().int().nonnegative(),
      messageType: z.enum(['image', 'video', 'document']),
    })
    .nullable()
    .optional(),
});

export const activityMutationResponseSchema = z.object({
  id: z.number(),
  leadId: z.number(),
  userId: z.number(),
  type: z.enum(activityTypes),
  content: z.string(),
  scheduledAt: dateLikeSchema,
  isCompleted: z.boolean(),
  createdAt: optionalDateLikeSchema,
  source: z.enum(activitySources).nullable().optional(),
});

export const notificationItemSchema = z.object({
  id: z.number(),
  message: z.string(),
  targetUrl: z.string().nullable().optional(),
  type: z.enum(notificationTypes),
  isRead: z.boolean(),
  createdAt: dateLikeSchema,
});

const leadTrackingSearchResultSchema = z.object({
  id: z.number(),
  leadCode: z.string(),
  trackingReference: z.string(),
  name: z.string(),
  company: z.string().nullable(),
  email: z.string(),
  phone: z.string(),
  status: z.enum(leadStatuses),
  assignedUserName: z.string().nullable(),
  createdByName: z.string().nullable(),
  nextFollowupDate: z.string().nullable(),
});

const leadTrackingSearchResponseSchema = z.object({
  page: z.number(),
  limit: z.number(),
  total: z.number(),
  results: z.array(leadTrackingSearchResultSchema),
});

const leadTrackingUserSchema = minimalUserPreviewSchema
  .extend({
    role: z.enum(userRoles).optional(),
  })
  .nullable();

const leadTrackingActivitySchema = z.object({
  id: z.number(),
  type: z.enum(activityTypes),
  content: z.string(),
  source: z.enum(activitySources).nullable().optional(),
  createdAt: dateLikeSchema,
  user: leadTrackingUserSchema,
});

const leadTrackingDetailSchema = z.object({
  lead: z.object({
    id: z.number(),
    name: z.string(),
    company: z.string().nullable(),
    email: z.string(),
    phone: z.string(),
    status: z.enum(leadStatuses),
    source: z.string(),
    value: z.number().nullable(),
    nextFollowupDate: dateLikeSchema,
    notes: z.string().nullable(),
    assignedUser: leadTrackingUserSchema,
    createdByUser: leadTrackingUserSchema,
    product: productPreviewSchema
      .extend({
        price: z.number().nullable().optional(),
      })
      .nullable(),
    trackingReference: z.string(),
    managerName: z.string().nullable(),
    createdAt: dateLikeSchema,
    updatedAt: dateLikeSchema,
  }),
  stats: z.object({
    daysSinceCreated: z.number(),
    daysSinceLastContact: z.number().nullable(),
    probabilityOfConversion: z.number(),
  }),
  lastActivity: leadTrackingActivitySchema.nullable(),
  activities: z.array(leadTrackingActivitySchema),
  assignmentHistory: z.array(
    z.object({
      id: z.number(),
      leadId: z.number(),
      reason: z.string().nullable(),
      changedAt: dateLikeSchema,
      previousAssignedUser: leadTrackingUserSchema,
      newAssignedUser: leadTrackingUserSchema,
      changedByUser: leadTrackingUserSchema,
    })
  ),
  timeline: z.array(
    z.object({
      id: z.string(),
      type: z.enum([
        'lead_created',
        'assigned',
        'activity',
        'followup_due',
        'followup_overdue',
        'closed',
      ]),
      title: z.string(),
      description: z.string(),
      createdAt: z.string(),
    })
  ),
});

const placeSearchExistingLeadSchema = z.object({
  id: z.number(),
  leadCode: z.string(),
  name: z.string(),
  company: z.string().nullable(),
  assignedUser: minimalUserPreviewSchema.nullable(),
  createdByUser: minimalUserPreviewSchema.nullable(),
});

const placeSearchResultSchema = z.object({
  name: z.string(),
  address: z.string(),
  placeId: z.string(),
  phone: z.string().optional().nullable(),
  email: z.string().optional().nullable(),
  website: z.string().optional().nullable(),
  rating: z.number().optional(),
  reviews: z.number().optional(),
  photoName: z.string().optional().nullable(),
  photoAttribution: z.string().optional().nullable(),
  businessStatus: z.string().optional().nullable(),
  primaryType: z.string().optional().nullable(),
  priceLevel: z.string().optional().nullable(),
  openNow: z.boolean().optional().nullable(),
  weekdayDescriptions: z.array(z.string()).optional(),
  googleMapsUri: z.string().optional().nullable(),
  existingLead: placeSearchExistingLeadSchema.nullable().optional(),
});

const placeSearchResponseSchema = z.union([
  z.array(placeSearchResultSchema),
  z.object({
    results: z.array(placeSearchResultSchema),
    hasMore: z.boolean(),
    total: z.number(),
  }),
]);

const contactMessageSchema = z.object({
  id: z.number(),
  name: z.string(),
  email: z.string(),
  phone: z.string().nullable(),
  subject: z.string(),
  message: z.string(),
  status: z.enum(['new', 'in_progress', 'resolved']),
  createdAt: z.union([z.string(), z.date()]),
  updatedAt: z.union([z.string(), z.date()]),
});

const settingsCategorySchema = z.object({
  id: z.number(),
  key: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  sortOrder: z.number(),
  createdAt: z.union([z.string(), z.date()]).optional(),
  updatedAt: z.union([z.string(), z.date()]).optional(),
  settingsCount: z.number().optional(),
  sections: z
    .array(
      z.object({
        key: z.string(),
        name: z.string(),
        description: z.string(),
        sortOrder: z.number(),
      })
    )
    .optional(),
});

const effectiveSettingSchema = z.object({
  id: z.number(),
  key: z.string(),
  sectionKey: z.string().nullable().optional(),
  label: z.string(),
  description: z.string().nullable().optional(),
  type: z.string(),
  value: z.unknown().nullable(),
  defaultValue: z.unknown().nullable(),
  allowedValues: z.unknown().nullable().optional(),
  validationRules: z.record(z.unknown()).nullable().optional(),
  isCompanyEditable: z.boolean(),
  isSuperAdminOnly: z.boolean(),
  isDangerous: z.boolean(),
  featureFlagKey: z.string().nullable().optional(),
  sortOrder: z.number(),
  scope: z.enum(['platform', 'company']),
  source: z.enum(['company', 'platform_default', 'env_fallback', 'catalog_default']),
  companyId: z.number().nullable(),
  updatedAt: z.string().nullable(),
  updatedBy: z
    .object({
      id: z.number(),
      name: z.string(),
    })
    .nullable(),
  canEdit: z.boolean(),
});

const settingsCategoryResponseSchema = z.object({
  success: z.boolean(),
  category: z.string(),
  categoryMeta: settingsCategorySchema,
  companyId: z.number().nullable(),
  settings: z.array(effectiveSettingSchema),
  dangerZone: z.array(effectiveSettingSchema),
});

const companySettingsSnapshotSchema = z.object({
  success: z.boolean(),
  companyId: z.number(),
  categories: z.array(settingsCategoryResponseSchema),
});

const bulkAssignInputSchema = z.object({
  leadIds: z.array(z.number()).min(1),
  assignedUserId: z.number(),
  reason: z.string().max(255).optional(),
});

const autoAssignInputSchema = z.object({
  leadIds: z.array(z.number()).min(1),
  strategy: z.enum(['manual', 'round_robin', 'least_loaded', 'best_conversion']),
  managerId: z.number().optional(),
  userId: z.number().optional(),
  reason: z.string().max(255).optional(),
});

const bulkAssignResponseSchema = z.object({
  message: z.string(),
  updatedCount: z.number(),
  assignments: z.array(
    z.object({
      leadId: z.number(),
      assignedUserId: z.number(),
      assignedUserName: z.string(),
    })
  ),
});

const savedReportFilterSchema = z.object({
  id: z.number(),
  userId: z.number(),
  scope: z.enum(['ADMIN', 'MANAGER']),
  name: z.string(),
  filtersJson: z.string(),
  createdAt: z.union([z.string(), z.date()]),
  updatedAt: z.union([z.string(), z.date()]),
});

const webPushSubscriptionSchema = z.object({
  endpoint: z.string().url(),
  expirationTime: z.number().nullable().optional(),
  keys: z.object({
    p256dh: z.string().min(1),
    auth: z.string().min(1),
  }),
  userAgent: z.string().optional(),
  platform: z.string().optional(),
});

const walletBalanceResponseSchema = z.object({
  balance: z.number(),
});

const walletTransactionActorSchema = z
  .object({
    id: z.number(),
    name: z.string(),
    username: z.string().nullable().optional(),
  })
  .nullable();

const walletTransactionItemSchema = z.object({
  id: z.number(),
  transactionType: walletTransactionTypeSchema,
  debitType: walletDebitTypeSchema.nullable(),
  amount: z.number(),
  balanceBefore: z.number(),
  balanceAfter: z.number(),
  description: z.string(),
  targetUrl: z.string().nullable().optional(),
  contextLabel: z.string().nullable().optional(),
  messagePreview: z.string().nullable().optional(),
  sourceType: walletSourceTypeSchema,
  queryText: z.string().nullable(),
  referenceEntityType: z.string().nullable(),
  referenceEntityId: z.number().nullable(),
  hasCreditReceipt: z.boolean(),
  createdAt: z.string(),
  performedByUser: walletTransactionActorSchema,
});

const walletTransactionsQuerySchema = paginationQuerySchema.extend({
  from: z.string().optional(),
  to: z.string().optional(),
  debitType: walletDebitTypeSchema.optional(),
  search: z.string().optional(),
});

const walletRechargeCreateOrderSchema = z.object({
  amount: z
    .number()
    .min(
      WALLET_MIN_RECHARGE_AMOUNT_INR,
      `Recharge amount must be at least INR ${WALLET_MIN_RECHARGE_AMOUNT_INR}.`
    ),
});

const walletRechargeOrderResponseSchema = z.object({
  orderId: z.string(),
  amount: z.number(),
  currency: z.string(),
  key: z.string(),
});

const walletRechargeVerifySchema = z.object({
  razorpay_order_id: z.string(),
  razorpay_payment_id: z.string(),
  razorpay_signature: z.string(),
});

const walletRechargeVerifyResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  balance: z.number(),
});

const walletCreditReceiptResponseSchema = z.object({
  documentType: z.literal('CREDIT_PAYMENT_RECEIPT'),
  receiptVoucherNumber: z.string().min(1).max(16),
  issuedAt: z.string(),
  billedTo: z.object({
    name: z.string(),
    email: z.string(),
    phone: z.string().nullable(),
    address: z.string(),
    city: z.string(),
    state: z.string(),
    stateCode: z.string(),
    postalCode: z.string(),
    country: z.string(),
    gstin: z.string().nullable(),
  }),
  issuedBy: z.object({
    name: z.string(),
    email: z.string().nullable(),
    address: z.string().nullable(),
    gstin: z.string().nullable(),
    sacCode: z.string().nullable(),
    state: z.string(),
    stateCode: z.string(),
  }),
  creditAmount: z.number(),
  totalPaid: z.number(),
  currency: z.literal('INR'),
  taxNote: z.string(),
  transaction: z.object({
    paymentId: z.string().nullable(),
    orderId: z.string(),
    status: z.literal('PAID'),
  }),
});

const razorpayWebhookResponseSchema = z.object({
  received: z.boolean(),
  event: z.string().optional(),
  processed: z.boolean().optional(),
  action: z.string().optional(),
  message: z.string().optional(),
});

const servicePricingItemSchema = z.object({
  serviceKey: serviceKeySchema,
  serviceName: z.string(),
  pricePerCall: z.number(),
  isActive: z.boolean(),
  description: z.string().nullable(),
  updatedAt: z.string(),
});

const servicePricingUpdateSchema = z.object({
  serviceName: z.string().min(1).optional(),
  pricePerCall: z.number().positive().optional(),
  isActive: z.boolean().optional(),
  description: z.string().nullable().optional(),
});

const notificationQueueDebugItemSchema = z.object({
  id: z.number(),
  eventKey: z.string(),
  templateKey: z.string(),
  channel: z.enum(['in_app', 'whatsapp', 'email']),
  status: z.enum(['pending', 'scheduled', 'processing', 'sent', 'failed', 'cancelled', 'skipped']),
  recipientId: z.number().nullable(),
  adminId: z.number().nullable(),
  phoneNumberSnapshot: z.string().nullable(),
  sourceEntityType: z.string().nullable(),
  sourceEntityId: z.number().nullable(),
  dedupeKey: z.string().nullable(),
  scheduledAt: z.string(),
  sentAt: z.string().nullable(),
  retryCount: z.number(),
  maxRetries: z.number(),
  failureReason: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const broadcastFiltersSchema = reportFiltersSchema.extend({
  status: z.string().optional(),
  statuses: z.array(z.string()).optional(),
  assignedUserId: z.coerce.number().optional(),
  unassignedOnly: z.coerce.boolean().optional(),
  onlyEscalated: z.coerce.boolean().optional(),
});

const broadcastRecipientGroupMemberInputSchema = z.object({
  id: z.number().optional(),
  leadId: z.number().optional().nullable(),
  name: z.string().trim().max(255).optional().nullable(),
  phoneNumber: z.string().trim().min(5).max(50),
  email: z.string().trim().email().optional().nullable().or(z.literal('')),
  notes: z.string().trim().max(1000).optional().nullable(),
});

const broadcastRecipientGroupUpsertSchema = z.object({
  name: z.string().trim().min(1).max(255),
  description: z.string().trim().max(2000).optional().nullable(),
  members: z.array(broadcastRecipientGroupMemberInputSchema).min(1),
});

const broadcastRecipientGroupFromLeadsSchema = z.object({
  name: z.string().trim().min(1).max(255),
  description: z.string().trim().max(2000).optional().nullable(),
  filtersJson: broadcastFiltersSchema.default({}),
});

const broadcastCampaignStatusSchema = z.enum([
  'draft',
  'scheduled',
  'sending',
  'completed',
  'failed',
]);

const broadcastRecipientStatusSchema = z.enum(['pending', 'sent', 'failed', 'replied']);

const conversationDirectionSchema = z.enum(['inbound', 'outbound']);
const conversationChannelSchema = z.enum(['whatsapp', 'facebook', 'instagram', 'threads']);
const socialInteractionTypeSchema = z.enum(['dm', 'comment', 'reply']);
const conversationMessageTypeSchema = z.enum(conversationMessageTypes);
const conversationMessageDeliveryStatusSchema = z.enum(['sent', 'delivered', 'read', 'failed']);

/**
 * Header attachment for a media-header template. Either a file uploaded through
 * `broadcasts.uploadMedia` (preferred: `mediaId` is reused for every recipient) or a
 * public HTTPS URL that Meta downloads per message.
 */
const broadcastTemplateHeaderMediaSchema = z
  .object({
    url: z.string().trim().url().max(2000).optional().nullable(),
    mediaId: z.string().trim().min(1).max(191).optional().nullable(),
    fileName: z.string().trim().max(255).optional().nullable(),
    mimeType: z.string().trim().max(120).optional().nullable(),
    sizeBytes: z.coerce.number().int().nonnegative().optional().nullable(),
    storageKey: z.string().trim().max(512).optional().nullable(),
  })
  .refine(value => Boolean(value.url || value.mediaId), {
    message: 'Attach a file or provide a public HTTPS URL for the header.',
  })
  .optional()
  .nullable();

const broadcastMediaUploadQuerySchema = z.object({
  whatsappAccountId: z.coerce.number().int().positive(),
  fileName: z.string().trim().min(1).max(255),
  mimeType: z.enum(whatsappMediaMimeTypes),
});

const broadcastMediaUploadResponseSchema = z.object({
  mediaId: z.string(),
  url: z.string().nullable(),
  storageKey: z.string().nullable(),
  fileName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  format: z.enum(whatsAppTemplateMediaHeaderFormatOptions),
  whatsappAccountId: z.number(),
  /** Meta keeps uploaded media for 30 days; scheduled sends beyond that fall back to the URL. */
  expiresAt: z.string(),
});

const broadcastCreateInputSchema = z.object({
  name: z.string().trim().min(1).max(255),
  templateId: z.coerce.number().int().positive(),
  whatsappAccountId: z.coerce.number().int().positive().optional(),
  filtersJson: broadcastFiltersSchema.default({}),
  recipientGroupIds: z.array(z.coerce.number().int().positive()).default([]),
  campaignVariables: z.record(z.string().max(2000)).default({}),
  templateHeaderMedia: broadcastTemplateHeaderMediaSchema,
  scheduledAt: z
    .union([z.string().pipe(z.coerce.date()), z.date()])
    .optional()
    .nullable(),
});

const broadcastPreviewInputSchema = broadcastCreateInputSchema.extend({
  previewPage: z.coerce.number().int().min(1).optional(),
  previewLimit: z.coerce.number().int().min(1).max(100).optional(),
});

const broadcastCampaignSummarySchema = z.object({
  id: z.number(),
  adminId: z.number(),
  createdByUserId: z.number(),
  name: z.string(),
  templateId: z.number().nullable(),
  whatsappAccountId: z.number().nullable(),
  filtersJson: z.record(z.unknown()).nullable(),
  totalRecipients: z.number(),
  status: broadcastCampaignStatusSchema,
  scheduledAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  templateTitle: z.string().nullable(),
  templateKey: z.string().nullable(),
  createdByName: z.string().nullable(),
  sentCount: z.number(),
  failedCount: z.number(),
  pendingCount: z.number(),
  repliedCount: z.number(),
  estimatedCost: z.number(),
  billingMode: z.literal('meta_direct').optional(),
});

const broadcastRecipientPreviewItemSchema = z.object({
  name: z.string().nullable(),
  phoneNumber: z.string(),
  email: z.string().nullable().optional(),
  source: z.string(),
});

const broadcastRenderedPreviewItemSchema = broadcastRecipientPreviewItemSchema.extend({
  text: z.string(),
  missingVariables: z.array(z.string()),
  templateMessage: whatsAppTemplateMessageSchema.optional(),
});

const broadcastPreviewResponseSchema = z.object({
  totalLeads: z.number(),
  validRecipients: z.number(),
  invalidRecipients: z.number(),
  estimatedCost: z.number(),
  walletBalance: z.number(),
  unitCost: z.number(),
  billingMode: z.literal('meta_direct').optional(),
  templateCategory: z.string().nullable().optional(),
  templatePreview: z.string().nullable(),
  templateTitle: z.string().nullable(),
  variableValidation: z.object({
    valid: z.boolean(),
    errors: z.array(z.string()),
    requiredVariables: z.array(
      z.object({
        key: z.string(),
        mappedKey: z.string().nullable(),
        ownership: z.enum(['crm', 'campaign']),
        missingCount: z.number(),
      })
    ),
    missingCampaignVariables: z.array(z.string()),
    missingRecipientVariables: z.array(
      z.object({
        key: z.string(),
        mappedKey: z.string().nullable(),
        missingCount: z.number(),
        sampleRecipients: z.array(
          z.object({
            name: z.string().nullable(),
            phoneNumber: z.string(),
            source: z.string(),
          })
        ),
      })
    ),
  }),
  recipientPreview: z.object({
    page: z.number(),
    limit: z.number(),
    total: z.number(),
    pageCount: z.number(),
    items: z.array(broadcastRecipientPreviewItemSchema),
    renderedPreviews: z.array(broadcastRenderedPreviewItemSchema),
  }),
  renderedPreviews: z.array(broadcastRenderedPreviewItemSchema),
  sampleRecipients: z.array(broadcastRecipientPreviewItemSchema),
});

const broadcastRecipientLeadSchema = z.object({
  id: z.number(),
  name: z.string(),
  company: z.string().nullable(),
  phone: z.string(),
  email: z.string(),
  assignedUserId: z.number().nullable(),
});

const broadcastRecipientItemSchema = z.object({
  id: z.number(),
  campaignId: z.number(),
  leadId: z.number().nullable(),
  recipientName: z.string().nullable(),
  recipientEmail: z.string().nullable(),
  phoneNumber: z.string(),
  source: z.string(),
  status: broadcastRecipientStatusSchema,
  sentAt: z.string().nullable(),
  errorMessage: z.string().nullable(),
  providerMessageId: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  lead: broadcastRecipientLeadSchema.nullable(),
});

const broadcastRecipientGroupSummarySchema = z.object({
  id: z.number(),
  adminId: z.number(),
  createdByUserId: z.number(),
  name: z.string(),
  description: z.string().nullable(),
  memberCount: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const broadcastRecipientGroupDetailSchema = broadcastRecipientGroupSummarySchema.extend({
  members: z.array(
    z.object({
      id: z.number(),
      groupId: z.number(),
      leadId: z.number().nullable(),
      name: z.string().nullable(),
      phoneNumber: z.string(),
      email: z.string().nullable(),
      notes: z.string().nullable(),
      createdAt: z.string(),
      updatedAt: z.string(),
    })
  ),
});

const conversationLeadSchema = z.object({
  id: z.number(),
  name: z.string(),
  company: z.string().nullable(),
  phone: z.string(),
  email: z.string(),
  status: z.string(),
  assignedUserId: z.number().nullable(),
});

const conversationAutomationStateSchema = z
  .object({
    id: z.number(),
    conversationId: z.number(),
    adminId: z.number(),
    isHumanTyping: z.boolean(),
    humanTypingExpiresAt: z.string().nullable(),
    humanTakeoverUntil: z.string().nullable(),
    takeoverVersion: z.number().int().nonnegative(),
    aiThinkingUntil: z.string().nullable(),
    lastHumanOutboundAt: z.string().nullable(),
    lastAiOutboundAt: z.string().nullable(),
    updatedByUserId: z.number().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
  })
  .nullable();

const conversationListItemSchema = z.object({
  id: z.number(),
  leadId: z.number().nullable(),
  adminId: z.number(),
  whatsappAccountId: z.number().nullable(),
  channel: conversationChannelSchema,
  socialAccountId: z.number().nullable(),
  externalParticipantId: z.string().nullable(),
  interactionType: socialInteractionTypeSchema.nullable(),
  sourceContentId: z.string().nullable(),
  sourceContext: z.unknown().nullable(),
  socialAssistantState: socialAssistantStateSchema.nullable().optional(),
  socialAiEnabled: z.boolean().optional(),
  contactName: z.string().nullable(),
  phoneNumber: z.string().nullable(),
  createdByUserId: z.number().nullable(),
  lastMessage: z.string().nullable(),
  lastMessageDirection: conversationDirectionSchema.nullable(),
  lastMessageType: conversationMessageTypeSchema.nullable(),
  lastMessageDeliveryStatus: conversationMessageDeliveryStatusSchema.nullable(),
  lastMessageAt: z.string().nullable(),
  replyWindowOpen: z.boolean(),
  replyWindowExpiresAt: z.string().nullable(),
  automationState: conversationAutomationStateSchema,
  unreadCount: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
  lead: conversationLeadSchema.nullable(),
});

const conversationMarkReadResponseSchema = z.object({
  conversationId: z.number(),
  unreadCount: z.number(),
  lastReadAt: z.string(),
});

const conversationMessageActorSchema = z
  .object({
    id: z.number(),
    name: z.string(),
  })
  .nullable();

const conversationMessageSchema = z.object({
  id: z.number(),
  conversationId: z.number(),
  direction: conversationDirectionSchema,
  messageType: conversationMessageTypeSchema,
  content: z.string(),
  providerMessageId: z.string().nullable(),
  socialContext: z.unknown().nullable(),
  providerPayload: z.unknown().nullable(),
  deliveryStatus: conversationMessageDeliveryStatusSchema.nullable(),
  deliveredAt: z.string().nullable(),
  readAt: z.string().nullable(),
  deliveryError: z.string().nullable(),
  createdByUserId: z.number().nullable(),
  createdAt: z.string(),
  createdByUser: conversationMessageActorSchema,
  media: z
    .object({
      url: z.string(),
      mimeType: z.enum(whatsappStoredMediaMimeTypes),
      fileName: z.string(),
      sizeBytes: z.number().int().nonnegative(),
    })
    .nullable(),
});

const sendConversationMessageInputSchema = z.object({
  conversationId: z.coerce.number().int().positive(),
  content: z.string().trim().min(1).max(4000),
});

const manageThreadsReplyInputSchema = z.object({
  hide: z.boolean(),
});

const conversationMediaFileSchema = z
  .object({
    fileName: z.string().trim().min(1).max(255),
    mimeType: z.enum(whatsappMediaMimeTypes),
    sizeBytes: z.coerce.number().int().positive(),
  })
  .superRefine((value, context) => {
    if (value.sizeBytes > getWhatsAppMediaMaxBytes(value.mimeType)) {
      context.addIssue({
        code: z.ZodIssueCode.too_big,
        maximum: getWhatsAppMediaMaxBytes(value.mimeType),
        inclusive: true,
        type: 'number',
        path: ['sizeBytes'],
        message: 'The selected file exceeds the WhatsApp size limit.',
      });
    }
  });

const createConversationMediaUploadInputSchema = conversationMediaFileSchema.and(
  z.object({
    conversationId: z.coerce.number().int().positive(),
  })
);

const sendConversationMediaInputSchema = conversationMediaFileSchema.and(
  z.object({
    conversationId: z.coerce.number().int().positive(),
    caption: z.string().trim().max(WHATSAPP_MEDIA_CAPTION_MAX_LENGTH).default(''),
    storageKey: z.string().trim().min(1).max(255),
  })
);

const conversationPresenceUpdateInputSchema = z.object({
  conversationId: z.coerce.number().int().positive(),
  isTyping: z.boolean(),
  takeOver: z.boolean().optional(),
  resume: z.boolean().optional(),
});

const conversationPresenceUpdateResponseSchema = z.object({
  success: z.boolean(),
});

const whatsappAccountUpsertSchema = z.object({
  wabaId: z.string().trim().min(1).max(191),
  phoneNumberId: z.string().trim().min(1).max(191),
  accessToken: z.string().trim().min(1),
  businessName: z.string().trim().max(255).optional().nullable(),
  isActive: z.boolean().optional(),
});

const socialAccountSummarySchema = z.object({
  id: z.number(),
  adminId: z.number(),
  platform: z.enum(['facebook', 'instagram', 'threads', 'google_business', 'youtube']),
  providerAccountId: z.string(),
  providerPageId: z.string().nullable(),
  assetName: z.string().nullable(),
  avatarUrl: z.string().url().nullable(),
  isActive: z.boolean(),
  tokenExpiresAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const GOOGLE_BUSINESS_REPLY_MAX_BYTES = 4096;

const googleBusinessLocationSchema = z.object({
  name: z.string().regex(/^accounts\/[^/]+\/locations\/[^/]+$/),
  accountName: z.string(),
  title: z.string(),
  address: z.string(),
});
const googleBusinessReviewReplySchema = z.object({
  comment: z.string(),
  updateTime: z.string().nullable(),
});
const googleBusinessReviewsSchema = z.object({
  items: z.array(
    z.object({
      id: z.string(),
      author: z.string().nullable(),
      rating: z.number().min(0).max(5),
      comment: z.string(),
      updatedAt: z.string().nullable(),
      reply: googleBusinessReviewReplySchema.nullable(),
    })
  ),
  averageRating: z.number().nullable(),
  totalReviewCount: z.number(),
  nextCursor: z.string().nullable(),
});

const socialPostPlatformSchema = z.enum([
  'facebook',
  'instagram',
  'threads',
  'google_business',
  'youtube',
]);
export const SOCIAL_POST_CAPTION_MAX_CHARACTERS = 2_200;
export const SOCIAL_POST_THREADS_CAPTION_MAX_CHARACTERS = 500;
const socialPostVideoPlatforms = ['facebook', 'instagram', 'threads', 'youtube'] as const;
const supportsSocialPostVideo = (platform: z.infer<typeof socialPostPlatformSchema>) =>
  (socialPostVideoPlatforms as readonly string[]).includes(platform);
const socialPostPublishInputSchema = z
  .object({
    message: z.string().trim().min(1).max(SOCIAL_POST_CAPTION_MAX_CHARACTERS),
    image: z
      .object({
        dataUrl: z.string().min(1).max(SETTINGS_IMAGE_DATA_URL_MAX_LENGTH),
        fileName: z.string().trim().min(1).max(255),
      })
      .optional()
      .nullable(),
    video: z
      .object({
        dataUrl: z.string().min(1).max(SOCIAL_POST_VIDEO_DATA_URL_MAX_LENGTH),
        fileName: z.string().trim().min(1).max(255),
      })
      .optional()
      .nullable(),
    generatedVideoPostId: z.coerce.number().int().positive().optional().nullable(),
    youtube: z
      .object({
        title: z.string().trim().min(1).max(100),
        privacyStatus: z.enum(['private', 'unlisted', 'public']),
      })
      .optional()
      .nullable(),
    platforms: z.array(socialPostPlatformSchema).min(1).max(5),
    accountIds: z
      .array(z.coerce.number().int().positive())
      .min(1)
      .max(100)
      .refine(ids => new Set(ids).size === ids.length, 'Select each social account only once.')
      .optional(),
  })
  .superRefine((input, ctx) => {
    const youtubeSelected = input.platforms.includes('youtube');
    const instagramSelected = input.platforms.includes('instagram');
    const videoPlatformSelected = input.platforms.some(supportsSocialPostVideo);

    const hasVideo = Boolean(input.video || input.generatedVideoPostId);

    if (instagramSelected && !input.image && !hasVideo) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['image'],
        message: 'Select an image or video to publish to Instagram.',
      });
    }
    if (youtubeSelected && !hasVideo) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['video'],
        message: 'Select a video to upload to YouTube.',
      });
    }
    if (youtubeSelected && !input.youtube) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['youtube'],
        message: 'Enter the YouTube video details.',
      });
    }
    if (
      input.platforms.includes('threads') &&
      input.message.length > SOCIAL_POST_THREADS_CAPTION_MAX_CHARACTERS
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['message'],
        message: `Threads posts must be ${SOCIAL_POST_THREADS_CAPTION_MAX_CHARACTERS} characters or fewer.`,
      });
    }
    if (hasVideo && !videoPlatformSelected) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['platforms'],
        message: 'Select Facebook, Instagram, Threads, or YouTube before adding a video.',
      });
    }
    if (input.youtube && !youtubeSelected) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['youtube'],
        message: 'Select YouTube before adding YouTube video details.',
      });
    }
    if (input.video && input.generatedVideoPostId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['generatedVideoPostId'],
        message: 'Choose either an uploaded video or a generated AI video.',
      });
    }
  });
const socialPostPublishResponseSchema = z.object({
  results: z.array(
    z.object({
      platform: socialPostPlatformSchema,
      accountId: z.number(),
      accountName: z.string().nullable(),
      success: z.boolean(),
      providerPostId: z.string().nullable(),
      message: z.string().nullable(),
    })
  ),
});
const socialPostCommentSchema = z.object({
  id: z.string(),
  author: z.string().nullable(),
  message: z.string(),
  publishedAt: z.string().datetime().nullable(),
  likeCount: z.number().int().nonnegative(),
  replyCount: z.number().int().nonnegative(),
});
const socialPostSummarySchema = z.object({
  id: z.string(),
  platform: socialPostPlatformSchema,
  accountName: z.string().nullable(),
  accountAvatarUrl: z.string().url().nullable(),
  message: z.string(),
  imageUrl: z.string().url().nullable(),
  videoUrl: z.string().url().nullable(),
  permalink: z.string().url().nullable(),
  publishedAt: z.string().datetime().nullable(),
  likeCount: z.number().int().nonnegative(),
  commentCount: z.number().int().nonnegative(),
  shareCount: z.number().int().nonnegative(),
  comments: z.array(socialPostCommentSchema),
});
const socialPostsListInputSchema = z.object({
  cursor: z.string().trim().min(1).max(4096).optional(),
  limit: z.coerce.number().int().min(1).max(25).optional(),
});
const socialPostsListResponseSchema = z.object({
  items: z.array(socialPostSummarySchema),
  nextCursor: z.string().nullable(),
});
const aiSocialPostPlatformSchema = z.enum(['facebook', 'instagram', 'threads']);
const aiSocialCostSummarySchema = z.object({
  estimatedCost: z.number(),
  actualCost: z.number(),
  operationCount: z.number().int(),
  cacheHits: z.number().int(),
  videoGenerations: z.number().int(),
  videoExtensions: z.number().int(),
  imageGenerations: z.number().int(),
  imageTransforms: z.number().int(),
  audioGenerations: z.number().int(),
  retries: z.number().int(),
});
const aiSocialPostSummarySchema = z.object({
  id: z.number().int().positive(),
  product: z.object({ id: z.number().int().positive(), name: z.string() }),
  source: z.enum(['automated', 'manual']),
  caption: z.string().nullable(),
  mediaUrl: z.string().url().nullable(),
  mediaType: z.enum(['image', 'video']),
  targetPlatforms: z.array(aiSocialPostPlatformSchema),
  generationStatus: z.enum(['scheduled', 'generating', 'ready', 'failed']),
  approvalStatus: z.enum(['not_required', 'pending', 'approved', 'rejected']),
  publishStatus: z.enum([
    'not_requested',
    'queued',
    'publishing',
    'published',
    'partially_published',
    'failed',
  ]),
  billing: z.record(z.unknown()),
  // Per-operation cost ledger summary; null for jobs created before the ledger existed.
  costSummary: aiSocialCostSummarySchema.nullable().optional(),
  publishResults: z.array(z.record(z.unknown())),
  errorMessage: z.string().nullable(),
  scheduledAt: z.string().datetime(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  publishedAt: z.string().datetime().nullable(),
});
const aiSocialPostsListInputSchema = z.object({
  status: z
    .enum(['all', 'pending_approval', 'scheduled', 'published', 'failed', 'rejected'])
    .optional(),
  limit: z.coerce.number().int().min(1).max(50).optional(),
});
const aiSocialPostsListResponseSchema = z.object({ items: z.array(aiSocialPostSummarySchema) });
const aiSocialPostCaptionInputSchema = z.object({ caption: z.string().trim().min(1).max(2200) });
const aiSocialManualVideoInputSchema = z.object({
  accountIds: z
    .array(z.coerce.number().int().positive())
    .min(1)
    .max(100)
    .refine(ids => new Set(ids).size === ids.length, 'Select each social account only once.'),
});

const gmailAccountSummarySchema = z.object({
  id: z.number().int().positive(),
  emailAddress: z.string().email(),
  isActive: z.boolean(),
  watchExpiresAt: z.string().datetime().nullable(),
  lastSyncedAt: z.string().datetime().nullable(),
  lastError: z.string().nullable(),
  createdAt: z.string().datetime(),
});

const gmailConnectResponseSchema = z.object({
  authorizationUrl: z.string().url(),
});

const gmailPubSubPushSchema = z.object({
  message: z.object({
    data: z.string().min(1),
    messageId: z.string().optional(),
    publishTime: z.string().optional(),
  }),
  subscription: z.string().optional(),
});

const whatsappAccountSummarySchema = z.object({
  id: z.number(),
  adminId: z.number(),
  wabaId: z.string(),
  metaBusinessId: z.string().nullable().optional(),
  phoneNumberId: z.string(),
  businessName: z.string().nullable(),
  displayPhoneNumber: z.string().nullable(),
  metaCatalogId: z.string().nullable().default(null),
  metaCatalogUrl: z.string().nullable().default(null),
  connectionMode: z.enum(['CLOUD_API', 'COEXISTENCE']).default('CLOUD_API'),
  contactSyncStatus: z.enum(['NOT_REQUESTED', 'REQUESTED', 'COMPLETED', 'FAILED']),
  historySyncStatus: z.enum(['NOT_REQUESTED', 'REQUESTED', 'COMPLETED', 'SKIPPED', 'FAILED']),
  historySyncProgress: z.number().int().min(0).max(100).nullable(),
  lastErrorCode: z.string().nullable().optional(),
  lastErrorMessage: z.string().nullable().optional(),
  isActive: z.boolean(),
  tokenCreatedAt: z.string().nullable(),
  tokenExpiresAt: z.string().nullable(),
  lastSyncedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  accessTokenMasked: z.string().nullable(),
  readiness: z
    .object({
      accountReviewStatus: z.string().nullable(),
      businessVerificationStatus: z.string().nullable(),
      phoneCodeVerificationStatus: z.string().nullable(),
      phoneNameStatus: z.string().nullable(),
      qualityRating: z.string().nullable(),
      platformType: z.string().nullable(),
      phoneStatus: z.string().nullable(),
      isOnBizApp: z.boolean().nullable(),
      messagingLimitTier: z.string().nullable().optional(),
      dailyConversationLimit: z.number().int().nullable().optional(),
      webhookSubscribed: z.boolean().nullable(),
      paymentMethodStatus: z.enum(['unknown', 'needs_confirmation']),
      paymentMethodMessage: z.string(),
      businessInfoMessage: z.string(),
      billingSettingsUrl: z.string().url(),
      businessSettingsUrl: z.string().url(),
      lastCheckedAt: z.string(),
      errors: z.array(z.string()),
    })
    .optional(),
});

/** Sending capacity of one connected number, read live from Meta. */
const whatsappAccountCapacitySchema = z.object({
  whatsappAccountId: z.number(),
  messagingLimitTier: z.string().nullable(),
  /** Business-initiated conversations allowed in a rolling 24h window; null = unlimited or unknown. */
  dailyConversationLimit: z.number().int().nullable(),
  /** Meta default is 80 messages/second per number; the dispatcher never exceeds this. */
  throughputMessagesPerSecond: z.number(),
  qualityRating: z.string().nullable(),
  checkedAt: z.string(),
  errors: z.array(z.string()),
});

const whatsappTemplateSampleMediaQuerySchema = z.object({
  whatsappAccountId: z.coerce.number().int().positive(),
  fileName: z.string().trim().min(1).max(255),
  mimeType: z.enum(whatsappMediaMimeTypes),
});

const whatsappAccountScopedQuerySchema = z
  .object({
    whatsappAccountId: z.coerce.number().int().positive().optional(),
  })
  .optional();

const whatsappConnectResponseSchema = z.object({
  appId: z.string().trim().min(1),
  configId: z.string().trim().min(1),
  featureType: z.string().trim(),
  graphVersion: z.string().trim().min(1),
  state: z.string().trim().min(1),
  signupUrl: z.string().url().optional(),
});

const whatsappConnectInputSchema = z.object({
  connectionMode: z.enum(['CLOUD_API', 'COEXISTENCE']).default('CLOUD_API'),
});

const whatsappCallbackQuerySchema = z
  .object({
    code: z.string().trim().min(1).optional(),
    state: z.string().trim().min(1),
    error: z.string().trim().optional(),
    error_description: z.string().trim().optional(),
  })
  .refine(data => data.code || data.error, {
    message: 'Meta callback must include a code or error',
  });

const whatsappEmbeddedSignupEventSchema = z.object({
  type: z.literal('WA_EMBEDDED_SIGNUP'),
  event: z.enum(['FINISH', 'FINISH_ONLY_WABA', 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING']),
  data: z.object({
    waba_id: z.string().trim().min(1),
    phone_number_id: z.string().trim().min(1).optional().nullable(),
    business_id: z.string().trim().min(1).optional().nullable(),
    page_ids: z.array(z.string()).optional(),
    catalog_ids: z.array(z.string()).optional(),
    dataset_ids: z.array(z.string()).optional(),
    instagram_account_ids: z.array(z.string()).optional(),
  }),
});

const whatsappEmbeddedSignupCompleteSchema = z
  .object({
    code: z.string().trim().min(1).optional(),
    accessToken: z.string().trim().min(1).optional(),
    state: z.string().trim().min(1),
    embeddedSignup: whatsappEmbeddedSignupEventSchema.optional(),
  })
  .refine(data => data.code || data.accessToken, {
    message: 'Either code or accessToken must be provided',
    path: ['code'],
  });

const whatsappEmbeddedSignupCompleteResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
});

const whatsappAccountUnlinkInputSchema = z.object({
  id: z.coerce.number().int().positive(),
});

const whatsappAccountUnlinkResponseSchema = z.object({
  success: z.literal(true),
  account: whatsappAccountSummarySchema.nullable(),
});

const metaSignedRequestInputSchema = z.object({
  signed_request: z.string().trim().min(1),
});

const metaDataDeletionResponseSchema = z.object({
  url: z.string().url(),
  confirmation_code: z.string(),
});

const metaDataDeletionStatusResponseSchema = z.object({
  confirmation_code: z.string().nullable(),
  status: z.literal('completed'),
  message: z.string(),
});

const metaDeauthorizeResponseSchema = z.object({
  success: z.boolean(),
  confirmation_code: z.string(),
  deletedConnections: z.number(),
});

const whatsappTemplateListItemSchema = z.object({
  id: z.number(),
  templateKey: z.string(),
  title: z.string(),
  language: z.string(),
  category: z.string(),
  wabaId: z.string().nullable().optional(),
  productId: z.number().nullable(),
  product: productPreviewSchema.nullable(),
  providerTemplateName: z.string().nullable(),
  metaParameterFormat: z.enum(['NAMED', 'POSITIONAL']).nullable(),
  bodyPreviewText: z.string().nullable(),
  components: z.array(whatsAppTemplateComponentSchema),
  variableDefinitions: z.array(templateVariableDefinitionSchema),
  supportedAudienceSources: z.array(z.enum(['leadflow', 'saved_contact_lists'])),
  unavailableSavedContactVariables: z.array(z.string()),
  isActive: z.boolean(),
});

const analyticsListSchema = <T extends z.ZodTypeAny>(itemSchema: T, maxItems = 50) =>
  z.preprocess(value => {
    if (Array.isArray(value)) {
      return value.flatMap(item => (typeof item === 'string' ? item.split(',') : [item]));
    }

    if (typeof value === 'string') {
      return value.split(',').filter(Boolean);
    }

    return value == null ? [] : [value];
  }, z.array(itemSchema).max(maxItems).default([]));

export const leadAnalyticsFiltersSchema = z
  .object({
    preset: z
      .enum(['last_7_days', 'last_30_days', 'this_month', 'last_month', 'custom'])
      .default('last_30_days'),
    from: optionalQueryDateSchema,
    to: optionalQueryDateSchema,
    assignedUserIds: analyticsListSchema(z.coerce.number().int().positive()),
    createdByUserIds: analyticsListSchema(z.coerce.number().int().positive()),
    productIds: analyticsListSchema(z.coerce.number().int().positive()),
    categoryIds: analyticsListSchema(z.coerce.number().int().positive()),
    sources: analyticsListSchema(z.string().trim().min(1).max(255)),
    statuses: analyticsListSchema(z.enum(leadStatuses)),
    pipelineStages: analyticsListSchema(z.enum(leadPipelineStages)),
    assignmentState: z.enum(['all', 'assigned', 'unassigned']).default('all'),
    followupState: z.enum(['all', 'scheduled', 'due_today', 'overdue', 'none']).default('all'),
    activityState: z.enum(['all', 'contacted', 'untouched']).default('all'),
    activityTypes: analyticsListSchema(z.enum(activityTypes)),
    activitySources: analyticsListSchema(z.enum(activitySources)),
    channels: analyticsListSchema(z.enum(conversationChannels)),
    broadcastStatuses: analyticsListSchema(z.enum(broadcastRecipientStatuses)),
    gmailImportState: z.enum(['all', 'imported', 'not_imported']).default('all'),
    minValue: optionalQueryNumberSchema,
    maxValue: optionalQueryNumberSchema,
    addressSearch: optionalQueryStringSchema,
    search: optionalQueryStringSchema,
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(25),
    sortBy: z
      .enum(['createdAt', 'value', 'nextFollowupDate', 'lastActivityAt'])
      .default('createdAt'),
    sortOrder: z.enum(['asc', 'desc']).default('desc'),
    groupBy: z.enum(['manager', 'salesman']).optional(),
  })
  .superRefine((value, ctx) => {
    if (value.preset === 'custom' && (!value.from || !value.to)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['from'],
        message: 'A custom report period needs both a start and end date.',
      });
    }

    if (value.from && value.to && value.from > value.to) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['to'],
        message: 'The report end date must be on or after the start date.',
      });
    }
  });

const leadAnalyticsPeriodSchema = z.object({
  preset: z.enum(['last_7_days', 'last_30_days', 'this_month', 'last_month', 'custom']),
  from: z.string().date(),
  to: z.string().date(),
  timeZone: z.string(),
});

const leadAnalyticsMetricSchema = z.object({
  count: z.number(),
  value: z.number(),
});

const leadAnalyticsBucketSchema = z.object({
  key: z.string(),
  label: z.string(),
  count: z.number(),
  value: z.number(),
});

const leadAnalyticsOverviewSchema = z.object({
  scope: z.enum(['ADMIN', 'MANAGER']),
  generatedAt: z.string(),
  period: leadAnalyticsPeriodSchema,
  filters: leadAnalyticsFiltersSchema,
  kpis: z.object({
    totalLeads: z.number(),
    assignedLeads: z.number(),
    unassignedLeads: z.number(),
    touchedLeads: z.number(),
    untouchedLeads: z.number(),
    activeOpenLeads: z.number(),
    openLeads: z.number(),
    convertedLeads: z.number(),
    lostLeads: z.number(),
    openPipelineValue: z.number(),
    closedWon: leadAnalyticsMetricSchema,
    closedLost: leadAnalyticsMetricSchema,
    conversionRate: z.number(),
    lostRate: z.number(),
    touchRate: z.number(),
    revenueWon: z.number(),
    overdueFollowups: z.number(),
    dueTodayFollowups: z.number(),
    contactedLeads: z.number(),
    contactCoverageRate: z.number(),
  }),
  trend: z.array(z.object({ date: z.string(), leads: z.number(), value: z.number() })),
  sources: z.array(leadAnalyticsBucketSchema),
  statusDistribution: z.array(leadAnalyticsBucketSchema),
});

const leadAnalyticsPipelineSchema = z.object({
  period: leadAnalyticsPeriodSchema,
  stages: z.array(leadAnalyticsBucketSchema),
  transitions: z.array(
    z.object({
      fromStage: z.string().nullable(),
      toStage: z.string(),
      count: z.number(),
    })
  ),
  cycleTime: z.object({
    trackedTransitions: z.number(),
    medianHoursInPreviousStage: z.number().nullable(),
  }),
  historyStartsAt: z.string().nullable(),
});

const leadAnalyticsTeamSchema = z.object({
  groupBy: z.enum(['manager', 'salesman']),
  items: z.array(
    z.object({
      userId: z.number().nullable(),
      name: z.string(),
      role: z.enum(userRoles).nullable(),
      leadCount: z.number(),
      openPipelineValue: z.number(),
      closedWon: z.number(),
      conversionRate: z.number(),
      overdueFollowups: z.number(),
      completedActivities: z.number(),
    })
  ),
});

const leadAnalyticsEngagementSchema = z.object({
  period: leadAnalyticsPeriodSchema,
  activities: z.array(leadAnalyticsBucketSchema),
  activitySources: z.array(leadAnalyticsBucketSchema),
  conversations: z.array(leadAnalyticsBucketSchema),
  messages: z.array(
    z.object({
      direction: z.enum(['inbound', 'outbound']),
      deliveryStatus: z.enum(messageDeliveryStatuses).nullable(),
      count: z.number(),
    })
  ),
  broadcasts: z.array(leadAnalyticsBucketSchema),
  gmailImportedLeads: z.number(),
});

const leadAnalyticsLeadItemSchema = z.object({
  id: z.number(),
  leadCode: z.string(),
  name: z.string(),
  company: z.string().nullable(),
  email: z.string(),
  phone: z.string(),
  source: z.string(),
  status: z.enum(leadStatuses),
  pipelineStage: z.enum(leadPipelineStages),
  value: z.number(),
  productName: z.string().nullable(),
  categoryName: z.string().nullable(),
  assignedUserName: z.string().nullable(),
  createdByUserName: z.string().nullable(),
  createdAt: z.string().nullable(),
  nextFollowupDate: z.string().nullable(),
  lastActivityAt: z.string().nullable(),
});

const leadAnalyticsLeadPageSchema = z.object({
  items: z.array(leadAnalyticsLeadItemSchema),
  page: z.number(),
  limit: z.number(),
  total: z.number(),
  pageCount: z.number(),
});

const leadAnalyticsFacetsSchema = z.object({
  users: z.array(
    minimalUserPreviewSchema.extend({
      role: z.enum(userRoles),
      managerId: z.number().nullable(),
    })
  ),
  products: z.array(productPreviewSchema),
  categories: z.array(leadCategoryPreviewSchema),
  sources: z.array(z.object({ value: z.string(), label: z.string(), count: z.number() })),
});

const leadAnalyticsSavedViewSchema = z.object({
  id: z.number(),
  name: z.string(),
  filters: leadAnalyticsFiltersSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const publicPlatformStatsSchema = z.object({
  activeTeams: z.number().int().nonnegative(),
  leadsProcessed: z.number().int().nonnegative(),
  regionsServed: z.number().int().nonnegative(),
  customerSatisfactionPercent: z.number().min(0).max(100).nullable(),
  generatedAt: z.string(),
});

export type PublicPlatformStats = z.infer<typeof publicPlatformStatsSchema>;

export const api = {
  auth: {
    login: {
      method: 'POST' as const,
      path: '/api/login',
      input: z.object({ username: z.string(), password: z.string() }),
      responses: {
        200: authUserSchema,
        401: errorSchemas.unauthorized,
      },
    },
    logout: {
      method: 'POST' as const,
      path: '/api/logout',
      responses: {
        200: z.void(),
      },
    },
    me: {
      method: 'GET' as const,
      path: '/api/user',
      responses: {
        200: authUserSchema,
        401: errorSchemas.unauthorized,
      },
    },
    register: {
      method: 'POST' as const,
      path: '/api/register',
      input: publicSignupSchema,
      responses: {
        201: authUserSchema,
        400: errorSchemas.validation,
      },
    },
    requestPasswordResetOtp: {
      method: 'POST' as const,
      path: '/api/password-reset/request-otp',
      input: z.object({
        username: z.string().trim().min(1, 'Username is required'),
      }),
      responses: {
        200: passwordResetRequestResponseSchema,
        400: errorSchemas.validation,
        503: errorSchemas.internal,
      },
    },
    verifyPasswordResetOtp: {
      method: 'POST' as const,
      path: '/api/password-reset/verify-otp',
      input: z.object({
        username: z.string().trim().min(1, 'Username is required'),
        otp: z
          .string()
          .trim()
          .regex(/^\d{4,8}$/, 'Enter a valid OTP'),
      }),
      responses: {
        200: passwordResetVerificationResponseSchema,
        400: errorSchemas.validation,
      },
    },
    resetPasswordWithOtp: {
      method: 'POST' as const,
      path: '/api/password-reset/reset',
      input: z.object({
        username: z.string().trim().min(1, 'Username is required'),
        resetToken: z.string().trim().min(1, 'Reset token is required'),
        newPassword: z.string().min(6, 'Password must be at least 6 characters'),
      }),
      responses: {
        200: passwordResetRequestResponseSchema,
        400: errorSchemas.validation,
      },
    },
  },
  userPreferences: {
    update: {
      method: 'PATCH' as const,
      path: '/api/user/preferences',
      input: userPreferencesSchema,
      responses: {
        200: authUserSchema,
        400: errorSchemas.validation,
        401: errorSchemas.unauthorized,
      },
    },
  },
  public: {
    stats: {
      method: 'GET' as const,
      path: '/api/public/stats',
      responses: {
        200: publicPlatformStatsSchema,
        500: errorSchemas.internal,
      },
    },
  },
  users: {
    list: {
      method: 'GET' as const,
      path: '/api/users',
      input: paginationQuerySchema
        .extend({
          search: z.string().optional(),
          status: userListStatusSchema.optional(),
        })
        .optional(),
      responses: {
        200: z.union([
          z.array(userListItemSchema),
          paginatedResponseSchema(userListItemSchema).extend({
            summary: z.object({
              total: z.number(),
              managers: z.number(),
              salesmen: z.number(),
              terminated: z.number(),
            }),
          }),
        ]),
      },
    },
    create: {
      method: 'POST' as const,
      path: '/api/users',
      input: insertUserSchema,
      responses: {
        201: userListItemSchema,
        400: errorSchemas.validation,
      },
    },
    update: {
      method: 'PUT' as const,
      path: '/api/users/:id',
      input: insertUserSchema.partial(),
      responses: {
        200: userListItemSchema,
        404: errorSchemas.notFound,
      },
    },
    delete: {
      method: 'DELETE' as const,
      path: '/api/users/:id',
      responses: {
        204: z.void(),
        404: errorSchemas.notFound,
      },
    },
  },
  products: {
    list: {
      method: 'GET' as const,
      path: '/api/products',
      input: paginationQuerySchema
        .extend({
          search: z.string().optional(),
          includeArchived: z.enum(['true', 'false', 'only']).optional(),
        })
        .optional(),
      responses: {
        200: z.union([
          z.array(productListItemSchema),
          paginatedResponseSchema(productListItemSchema),
        ]),
      },
    },
    get: {
      method: 'GET' as const,
      path: '/api/products/:id',
      responses: {
        200: productListItemSchema,
        404: errorSchemas.notFound,
      },
    },
    create: {
      method: 'POST' as const,
      path: '/api/products',
      input: insertProductSchema,
      responses: {
        201: productListItemSchema,
        400: errorSchemas.validation,
      },
    },
    update: {
      method: 'PUT' as const,
      path: '/api/products/:id',
      input: insertProductUpdateSchema,
      responses: {
        200: productListItemSchema,
        404: errorSchemas.notFound,
      },
    },
    delete: {
      method: 'DELETE' as const,
      path: '/api/products/:id',
      responses: {
        204: z.void(),
        409: errorSchemas.validation,
        404: errorSchemas.notFound,
      },
    },
    archive: {
      method: 'POST' as const,
      path: '/api/products/:id/archive',
      responses: {
        200: productListItemSchema,
        404: errorSchemas.notFound,
        409: errorSchemas.validation,
      },
    },
    restore: {
      method: 'POST' as const,
      path: '/api/products/:id/restore',
      responses: {
        200: productListItemSchema,
        404: errorSchemas.notFound,
        409: errorSchemas.validation,
      },
    },
    metaCatalogStatus: {
      method: 'GET' as const,
      path: '/api/products/meta-catalog/status',
      responses: {
        200: productMetaCatalogStatusSchema,
        403: z.object({ message: z.string() }),
        409: errorSchemas.internal,
        502: errorSchemas.internal,
        503: errorSchemas.internal,
      },
    },
    createMetaCatalog: {
      method: 'POST' as const,
      path: '/api/products/meta-catalog',
      input: productMetaCatalogCreateInputSchema,
      responses: {
        201: productMetaCatalogCreateResultSchema,
        403: z.object({ message: z.string() }),
        409: errorSchemas.internal,
        502: errorSchemas.internal,
        503: errorSchemas.internal,
      },
    },
    deleteMetaCatalog: {
      method: 'DELETE' as const,
      path: '/api/products/meta-catalog/:catalogId',
      responses: {
        204: productMetaCatalogDeleteResultSchema,
        403: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
        409: errorSchemas.internal,
        502: errorSchemas.internal,
        503: errorSchemas.internal,
      },
    },
    syncMetaCatalog: {
      method: 'POST' as const,
      path: '/api/products/meta-catalog/sync',
      input: productCatalogSyncInputSchema,
      responses: {
        200: productCatalogSyncResultSchema,
        403: z.object({ message: z.string() }),
        409: errorSchemas.internal,
        502: errorSchemas.internal,
        503: errorSchemas.internal,
      },
    },
    linkMetaCatalog: {
      method: 'POST' as const,
      path: '/api/products/meta-catalog/link-whatsapp',
      input: productMetaCatalogLinkInputSchema,
      responses: {
        200: productMetaCatalogLinkResultSchema,
        403: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
        409: errorSchemas.internal,
        502: errorSchemas.internal,
        503: errorSchemas.internal,
      },
    },
    uploadMedia: {
      method: 'POST' as const,
      path: '/api/products/:id/media',
      input: z.object({
        fileName: z.string().trim().min(1).max(255),
        dataUrl: z.string().min(1).max(PRODUCT_IMAGE_DATA_URL_MAX_LENGTH),
        altText: z.string().trim().max(500).optional(),
      }),
      responses: {
        201: productMediaItemSchema,
        400: errorSchemas.validation,
        404: errorSchemas.notFound,
      },
    },
    uploadVideo: {
      method: 'POST' as const,
      path: '/api/products/:id/video',
      // Raw file body; filename comes from the query and MIME type from Content-Type.
      rawInput: z.object({
        fileName: z.string().trim().min(1).max(255),
        mimeType: z.enum(productVideoMimeTypes),
      }),
      // Retain legacy JSON clients within the general JSON request-body limit.
      input: z.object({
        fileName: z.string().trim().min(1).max(255),
        dataUrl: z.string().min(1).max(PRODUCT_VIDEO_DATA_URL_MAX_LENGTH),
      }),
      responses: {
        201: productMediaItemSchema,
        400: errorSchemas.validation,
        404: errorSchemas.notFound,
        413: errorSchemas.validation,
        422: errorSchemas.validation,
        503: errorSchemas.internal,
      },
    },
    deleteMedia: {
      method: 'DELETE' as const,
      path: '/api/products/:id/media/:mediaId',
      responses: {
        204: z.void(),
        404: errorSchemas.notFound,
      },
    },
  },
  productIntegration: {
    status: {
      method: 'GET' as const,
      path: '/api/settings/api-integration/product-sync',
      responses: { 200: productIntegrationKeyStatusSchema, 403: errorSchemas.validation },
    },
    rotateKey: {
      method: 'POST' as const,
      path: '/api/settings/api-integration/product-sync/key',
      responses: {
        201: productIntegrationKeyStatusSchema.extend({ apiKey: z.string().min(1) }),
        403: errorSchemas.validation,
      },
    },
    revokeKey: {
      method: 'DELETE' as const,
      path: '/api/settings/api-integration/product-sync/key',
      responses: { 204: z.void(), 403: errorSchemas.validation },
    },
    receive: {
      method: 'POST' as const,
      path: '/api/integrations/products',
      input: productSyncWebhookSchema,
      responses: {
        200: z.object({
          success: z.literal(true),
          operation: z.enum(['created', 'updated', 'deleted', 'not_found']),
          productId: z.string(),
          leaderDaddyProductId: z.number().nullable(),
          imageCount: z.number(),
        }),
        400: errorSchemas.validation,
        401: errorSchemas.unauthorized,
        413: errorSchemas.validation,
        422: errorSchemas.validation,
        429: errorSchemas.validation,
        502: errorSchemas.internal,
      },
    },
  },
  leadCategories: {
    list: {
      method: 'GET' as const,
      path: '/api/lead-categories',
      input: paginationQuerySchema
        .extend({
          search: z.string().optional(),
        })
        .optional(),
      responses: {
        200: z.union([
          z.array(leadCategoryListItemSchema),
          paginatedResponseSchema(leadCategoryListItemSchema),
        ]),
      },
    },
    create: {
      method: 'POST' as const,
      path: '/api/lead-categories',
      input: insertLeadCategorySchema,
      responses: {
        201: leadCategoryListItemSchema,
        400: errorSchemas.validation,
        409: errorSchemas.validation,
      },
    },
    update: {
      method: 'PUT' as const,
      path: '/api/lead-categories/:id',
      input: insertLeadCategorySchema
        .partial()
        .refine(
          (value: Record<string, unknown>) => Object.keys(value).length > 0,
          'Provide at least one category field.'
        ),
      responses: {
        200: leadCategoryListItemSchema,
        400: errorSchemas.validation,
        403: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
        409: errorSchemas.validation,
      },
    },
    delete: {
      method: 'DELETE' as const,
      path: '/api/lead-categories/:id',
      responses: {
        204: z.void(),
        403: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
      },
    },
  },
  settings: {
    get: {
      method: 'GET' as const,
      path: '/api/settings',
      responses: {
        200: z.record(z.unknown()),
      },
    },
    update: {
      method: 'POST' as const,
      path: '/api/settings',
      input: z.record(z.unknown()),
      responses: {
        200: z.record(z.unknown()),
      },
    },
    clientRuntime: {
      method: 'GET' as const,
      path: '/api/settings/client-runtime',
      responses: {
        200: z.object({
          analyticsId: z.string().nullable(),
          analyticsEnabled: z.boolean(),
        }),
      },
    },
  },
  adminSettings: {
    categories: {
      method: 'GET' as const,
      path: '/api/admin/settings/categories',
      responses: {
        200: z.object({
          success: z.boolean(),
          categories: z.array(settingsCategorySchema),
        }),
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
      },
    },
    getCategory: {
      method: 'GET' as const,
      path: '/api/admin/settings/:category',
      input: z
        .object({
          companyId: z.coerce.number().optional(),
        })
        .optional(),
      responses: {
        200: settingsCategoryResponseSchema,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
      },
    },
    updateCategory: {
      method: 'PUT' as const,
      path: '/api/admin/settings/:category',
      input: z.object({
        companyId: z.coerce.number().optional(),
        values: z.record(z.unknown()),
      }),
      responses: {
        200: settingsCategoryResponseSchema,
        400: errorSchemas.validation,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
      },
    },
    resetSetting: {
      method: 'POST' as const,
      path: '/api/admin/settings/reset/:key',
      input: z
        .object({
          companyId: z.coerce.number().optional(),
        })
        .optional(),
      responses: {
        200: z.object({
          success: z.boolean(),
          key: z.string(),
          scope: z.enum(['platform', 'company']),
        }),
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
      },
    },
    getCompany: {
      method: 'GET' as const,
      path: '/api/admin/settings/company/:companyId',
      responses: {
        200: companySettingsSnapshotSchema,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
      },
    },
    exportCompany: {
      method: 'GET' as const,
      path: '/api/admin/settings/export/:companyId',
      responses: {
        200: z.object({
          success: z.boolean(),
          companyId: z.number(),
          exportedAt: z.string(),
          settings: z.record(z.unknown()),
        }),
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
      },
    },
    importCompany: {
      method: 'POST' as const,
      path: '/api/admin/settings/import/:companyId',
      input: z.object({
        settings: z.record(z.unknown()),
      }),
      responses: {
        200: companySettingsSnapshotSchema,
        400: errorSchemas.validation,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
      },
    },
    cloneCompany: {
      method: 'POST' as const,
      path: '/api/admin/settings/clone/:companyId',
      input: z.object({
        sourceCompanyId: z.coerce.number(),
      }),
      responses: {
        200: companySettingsSnapshotSchema,
        400: errorSchemas.validation,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
      },
    },
    auditHistory: {
      method: 'GET' as const,
      path: '/api/admin/settings/audit',
      input: z
        .object({
          companyId: z.coerce.number().optional(),
          key: z.string().optional(),
          limit: z.coerce.number().optional(),
        })
        .optional(),
      responses: {
        200: z.object({
          success: z.boolean(),
          logs: z.array(
            z.object({
              id: z.number(),
              companyId: z.number().nullable(),
              scope: z.enum(['platform', 'company']),
              previousValue: z.string().nullable(),
              newValue: z.string().nullable(),
              changeReason: z.string().nullable(),
              createdAt: z.union([z.string(), z.date()]),
              key: z.string().nullable(),
              label: z.string().nullable(),
              changedById: z.number().nullable(),
              changedByName: z.string().nullable(),
            })
          ),
        }),
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
      },
    },
    uploadImage: {
      method: 'POST' as const,
      path: '/api/admin/settings/upload-image',
      input: z.object({
        key: z.string().min(1).max(120),
        fileName: z.string().min(1).max(255),
        dataUrl: z.string().min(1).max(SETTINGS_IMAGE_DATA_URL_MAX_LENGTH),
      }),
      responses: {
        200: z.object({
          success: z.boolean(),
          url: z.string(),
          fileName: z.string(),
          mimeType: z.string(),
          size: z.number(),
        }),
        400: errorSchemas.validation,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
      },
    },
    image: {
      method: 'GET' as const,
      path: '/api/admin/settings/image',
      input: z.object({ key: z.string().min(1).max(1024) }),
      responses: {
        200: z.unknown(),
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
      },
    },
  },
  export: {
    leads: {
      method: 'GET' as const,
      path: '/api/export/leads',
      responses: {
        200: z.string(), // CSV content
      },
    },
  },
  leads: {
    list: {
      method: 'GET' as const,
      path: '/api/leads',
      input: reportFiltersSchema
        .extend({
          status: z.enum(leadStatuses).optional(),
          assignedUserId: z.string().optional().or(z.coerce.number().transform(String).optional()),
          productId: z
            .union([
              z.literal('unassigned'),
              z.coerce.number().int().positive().transform(String),
              z.string(),
            ])
            .optional(),
          categoryId: z
            .union([
              z.literal('unassigned'),
              z.coerce.number().int().positive().transform(String),
              z.string(),
            ])
            .optional(),
          todayFollowupStatus: leadTodayFollowupStatusSchema.optional(),
          followupCompletion: z.enum(['pending', 'completed']).optional(),
          followupPurpose: leadFollowupPurposeSchema.optional(),
          followupFrom: optionalQueryDateTimeSchema,
          followupTo: optionalQueryDateTimeSchema,
          sortBy: z.enum(['createdAt', 'updatedAt', 'nextFollowupDate']).optional(),
          sortOrder: z.enum(['asc', 'desc']).optional(),
          unassignedOnly: z.coerce.boolean().optional(),
          onlyEscalated: z.coerce.boolean().optional(),
        })
        .optional(),
      responses: {
        200: z.union([z.array(leadListItemSchema), paginatedResponseSchema(leadListItemSchema)]),
      },
    },
    kanban: {
      method: 'GET' as const,
      path: '/api/leads/kanban',
      input: reportFiltersSchema
        .extend({
          stage: leadPipelineStageSchema.optional(),
          status: z.enum(leadStatuses).optional(),
          assignedUserId: z.string().optional().or(z.coerce.number().transform(String).optional()),
          productId: z
            .union([
              z.literal('unassigned'),
              z.coerce.number().int().positive().transform(String),
              z.string(),
            ])
            .optional(),
          categoryId: z
            .union([
              z.literal('unassigned'),
              z.coerce.number().int().positive().transform(String),
              z.string(),
            ])
            .optional(),
          todayFollowupStatus: leadTodayFollowupStatusSchema.optional(),
          unassignedOnly: z.coerce.boolean().optional(),
          onlyEscalated: z.coerce.boolean().optional(),
          sortBy: z.enum(['createdAt', 'updatedAt', 'nextFollowupDate']).optional(),
          sortOrder: z.enum(['asc', 'desc']).optional(),
        })
        .optional(),
      responses: {
        200: z.union([leadKanbanResponseSchema, leadKanbanStageResponseSchema]),
      },
    },
    followupAvailability: {
      method: 'GET' as const,
      path: '/api/leads/followup-calendar/availability',
      input: followupAvailabilityInputSchema,
      responses: {
        200: followupAvailabilityResponseSchema,
        400: errorSchemas.validation,
        403: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
      },
    },
    get: {
      method: 'GET' as const,
      path: '/api/leads/:id',
      responses: {
        200: leadDetailSchema,
        404: errorSchemas.notFound,
      },
    },
    assignmentHistory: {
      method: 'GET' as const,
      path: '/api/leads/:id/assignment-history',
      responses: {
        200: z.array(leadAssignmentHistoryItemSchema),
        404: errorSchemas.notFound,
      },
    },
    create: {
      method: 'POST' as const,
      path: '/api/leads',
      input: insertLeadSchema,
      responses: {
        201: leadDetailSchema,
        400: errorSchemas.validation,
        409: followupAvailabilityResponseSchema.extend({ message: z.string() }),
      },
    },
    update: {
      method: 'PUT' as const,
      path: '/api/leads/:id',
      input: insertLeadSchema.partial(),
      responses: {
        200: leadDetailSchema,
        400: errorSchemas.validation,
        403: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
        409: z.union([
          followupAvailabilityResponseSchema.extend({ message: z.string() }),
          errorSchemas.validation,
        ]),
      },
    },
    updatePipelineStage: {
      method: 'PATCH' as const,
      path: '/api/leads/:id/pipeline-stage',
      input: leadPipelineStageUpdateInputSchema,
      responses: {
        200: leadDetailSchema,
        400: errorSchemas.validation,
        403: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
        409: errorSchemas.validation,
      },
    },
    delete: {
      method: 'DELETE' as const,
      path: '/api/leads/:id',
      responses: {
        204: z.void(),
        404: errorSchemas.notFound,
      },
    },
    bulkImport: {
      method: 'POST' as const,
      path: '/api/leads/bulk',
      input: z.array(insertLeadSchema),
      responses: {
        201: z.array(leadDetailSchema),
      },
    },
    bulkAssign: {
      method: 'POST' as const,
      path: '/api/leads/bulk-assign',
      input: bulkAssignInputSchema,
      responses: {
        200: bulkAssignResponseSchema,
        400: errorSchemas.validation,
        403: z.object({ message: z.string() }),
      },
    },
    autoAssign: {
      method: 'POST' as const,
      path: '/api/leads/auto-assign',
      input: autoAssignInputSchema,
      responses: {
        200: bulkAssignResponseSchema.extend({
          candidates: z.array(
            z.object({
              id: z.number(),
              name: z.string(),
              role: z.string(),
              managerId: z.number().nullable(),
              openLeadCount: z.number(),
              conversionRate: z.number(),
            })
          ),
        }),
        400: errorSchemas.validation,
        403: z.object({ message: z.string() }),
      },
    },
  },
  activities: {
    list: {
      method: 'GET' as const,
      path: '/api/activities',
      input: z
        .object({
          leadId: z.string().optional(),
          userId: z.string().optional(),
          assignedUserId: z.string().optional().or(z.coerce.number().transform(String).optional()),
          productId: z.coerce.number().optional(),
          status: z.enum(leadStatuses).optional(),
          type: z.enum(activityTypes).optional(),
          search: z.string().optional(),
          from: z.coerce.date().optional(),
          to: z.coerce.date().optional(),
          page: z.coerce.number().optional(),
          limit: z.coerce.number().optional(),
        })
        .optional(),
      responses: {
        200: z.union([
          z.array(activityListItemSchema),
          paginatedResponseSchema(activityListItemSchema),
        ]),
      },
    },
    create: {
      method: 'POST' as const,
      path: '/api/activities',
      input: insertActivitySchema,
      responses: {
        201: activityMutationResponseSchema,
      },
    },
    update: {
      method: 'PUT' as const,
      path: '/api/activities/:id',
      input: insertActivitySchema.partial(),
      responses: {
        200: activityMutationResponseSchema,
      },
    },
  },
  notifications: {
    list: {
      method: 'GET' as const,
      path: '/api/notifications',
      query: z.object({
        page: z.coerce.number().int().min(1).optional().default(1),
        limit: z.coerce.number().int().min(1).max(50).optional().default(10),
      }),
      responses: {
        200: z.object({
          items: z.array(notificationItemSchema),
          hasMore: z.boolean(),
        }),
      },
    },
    markRead: {
      method: 'PUT' as const,
      path: '/api/notifications/:id/read',
      responses: {
        200: notificationItemSchema,
      },
    },
    markAllRead: {
      method: 'PUT' as const,
      path: '/api/notifications/read-all',
      responses: {
        200: z.object({ updatedCount: z.number() }),
      },
    },
  },
  push: {
    config: {
      method: 'GET' as const,
      path: '/api/push/config',
      responses: {
        200: z.object({
          enabled: z.boolean(),
          publicKey: z.string().nullable(),
        }),
      },
    },
    subscribe: {
      method: 'POST' as const,
      path: '/api/push/subscribe',
      input: webPushSubscriptionSchema,
      responses: {
        200: z.object({ success: z.boolean() }),
        400: errorSchemas.validation,
        401: errorSchemas.unauthorized,
      },
    },
    unsubscribe: {
      method: 'POST' as const,
      path: '/api/push/unsubscribe',
      input: z.object({
        endpoint: z.string().url().optional(),
      }),
      responses: {
        200: z.object({ success: z.boolean() }),
        401: errorSchemas.unauthorized,
      },
    },
    test: {
      method: 'POST' as const,
      path: '/api/push/test',
      responses: {
        200: z.object({
          success: z.boolean(),
          message: z.string(),
        }),
        401: errorSchemas.unauthorized,
      },
    },
  },
  reports: {
    generateWeeklyReport: {
      method: 'POST' as const,
      path: '/api/reports/admin/generate-weekly',
      input: z
        .object({
          dryRun: z.boolean().optional(),
          adminId: z.number().optional(),
        })
        .optional(),
      responses: {
        200: z.object({ message: z.string(), results: z.unknown().optional() }),
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
      },
    },
    savedFilters: {
      method: 'GET' as const,
      path: '/api/reports/saved-filters',
      responses: {
        200: z.array(savedReportFilterSchema),
        401: errorSchemas.unauthorized,
      },
    },
    saveFilter: {
      method: 'POST' as const,
      path: '/api/reports/saved-filters',
      input: z.object({
        scope: z.enum(['ADMIN', 'MANAGER']),
        name: z.string().min(1).max(191),
        filters: reportFiltersSchema,
      }),
      responses: {
        200: savedReportFilterSchema,
        400: errorSchemas.validation,
        401: errorSchemas.unauthorized,
      },
    },
    deleteSavedFilter: {
      method: 'DELETE' as const,
      path: '/api/reports/saved-filters/:id',
      responses: {
        204: z.void(),
        401: errorSchemas.unauthorized,
      },
    },
  },
  leadAnalytics: {
    facets: {
      method: 'GET' as const,
      path: '/api/lead-analytics/facets',
      input: leadAnalyticsFiltersSchema.optional(),
      responses: {
        200: leadAnalyticsFacetsSchema,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
      },
    },
    overview: {
      method: 'GET' as const,
      path: '/api/lead-analytics/overview',
      input: leadAnalyticsFiltersSchema.optional(),
      responses: {
        200: leadAnalyticsOverviewSchema,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
      },
    },
    pipeline: {
      method: 'GET' as const,
      path: '/api/lead-analytics/pipeline',
      input: leadAnalyticsFiltersSchema.optional(),
      responses: {
        200: leadAnalyticsPipelineSchema,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
      },
    },
    team: {
      method: 'GET' as const,
      path: '/api/lead-analytics/team',
      input: leadAnalyticsFiltersSchema.optional(),
      responses: {
        200: leadAnalyticsTeamSchema,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
      },
    },
    engagement: {
      method: 'GET' as const,
      path: '/api/lead-analytics/engagement',
      input: leadAnalyticsFiltersSchema.optional(),
      responses: {
        200: leadAnalyticsEngagementSchema,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
      },
    },
    leads: {
      method: 'GET' as const,
      path: '/api/lead-analytics/leads',
      input: leadAnalyticsFiltersSchema.optional(),
      responses: {
        200: leadAnalyticsLeadPageSchema,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
      },
    },
    exportLeads: {
      method: 'GET' as const,
      path: '/api/lead-analytics/leads/export',
      input: leadAnalyticsFiltersSchema.optional(),
      responses: {
        200: z.string(),
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
      },
    },
    savedViews: {
      method: 'GET' as const,
      path: '/api/lead-analytics/saved-views',
      responses: {
        200: z.array(leadAnalyticsSavedViewSchema),
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
      },
    },
    saveView: {
      method: 'POST' as const,
      path: '/api/lead-analytics/saved-views',
      input: z.object({
        name: z.string().trim().min(1).max(191),
        filters: leadAnalyticsFiltersSchema,
      }),
      responses: {
        200: leadAnalyticsSavedViewSchema,
        400: errorSchemas.validation,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
      },
    },
    deleteSavedView: {
      method: 'DELETE' as const,
      path: '/api/lead-analytics/saved-views/:id',
      responses: {
        204: z.void(),
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
      },
    },
  },
  leadTracking: {
    search: {
      method: 'GET' as const,
      path: '/api/lead-tracking/search',
      input: z.object({
        query: z.string(),
        page: z.coerce.number().optional(),
        limit: z.coerce.number().optional(),
      }),
      responses: {
        200: leadTrackingSearchResponseSchema,
        401: errorSchemas.unauthorized,
      },
    },
    detail: {
      method: 'GET' as const,
      path: '/api/lead-tracking/:leadId',
      responses: {
        200: leadTrackingDetailSchema,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
      },
    },
  },
  whatsAppTemplateAdmin: {
    list: {
      method: 'GET' as const,
      path: '/api/admin/whatsapp-templates',
      input: z
        .object({
          search: z.string().optional(),
          lifecycleStatus: z.string().optional(),
          language: z.string().optional(),
          metaCategory: z.string().optional(),
          syncStatus: z.string().optional(),
          source: z.string().optional(),
          productId: z.coerce.number().int().positive().optional(),
          includeInactive: z.coerce.boolean().optional(),
        })
        .optional(),
      responses: {
        200: z.array(whatsAppTemplateSummarySchema),
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
      },
    },
    get: {
      method: 'GET' as const,
      path: '/api/admin/whatsapp-templates/:id',
      responses: {
        200: whatsAppTemplateDetailSchema,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
      },
    },
    create: {
      method: 'POST' as const,
      path: '/api/admin/whatsapp-templates',
      input: whatsAppTemplateDraftSchema,
      responses: {
        200: whatsAppTemplateMutationResponseSchema,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
      },
    },
    update: {
      method: 'PATCH' as const,
      path: '/api/admin/whatsapp-templates/:id',
      input: whatsAppTemplateDraftSchema.partial(),
      responses: {
        200: whatsAppTemplateMutationResponseSchema,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
      },
    },
    delete: {
      method: 'DELETE' as const,
      path: '/api/admin/whatsapp-templates/:id',
      input: z.object({
        scope: whatsAppTemplateDeleteScopeSchema,
      }),
      responses: {
        200: whatsAppTemplateDeleteResponseSchema,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
      },
    },
    duplicate: {
      method: 'POST' as const,
      path: '/api/admin/whatsapp-templates/:id/duplicate',
      input: z
        .object({
          templateKey: templateKeySchema.optional(),
          providerTemplateName: templateNameSchema.optional(),
          title: z.string().max(255).optional(),
        })
        .optional(),
      responses: {
        200: whatsAppTemplateMutationResponseSchema,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
      },
    },
    preview: {
      method: 'POST' as const,
      path: '/api/admin/whatsapp-templates/preview',
      input: whatsAppTemplateDraftSchema,
      responses: {
        200: whatsAppTemplatePreviewSchema,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
      },
    },
    validate: {
      method: 'POST' as const,
      path: '/api/admin/whatsapp-templates/validate',
      input: whatsAppTemplateDraftSchema,
      responses: {
        200: whatsAppTemplateValidationSchema,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
      },
    },
    push: {
      method: 'POST' as const,
      path: '/api/admin/whatsapp-templates/:id/push',
      responses: {
        200: whatsAppTemplateMutationResponseSchema,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
      },
    },
    syncMeta: {
      method: 'POST' as const,
      path: '/api/admin/whatsapp-templates/sync-meta',
      responses: {
        200: whatsAppTemplateSyncResponseSchema,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
      },
    },
    refreshMeta: {
      method: 'POST' as const,
      path: '/api/admin/whatsapp-templates/:id/refresh-meta',
      responses: {
        200: whatsAppTemplateMutationResponseSchema,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
      },
    },
    updateActive: {
      method: 'PATCH' as const,
      path: '/api/admin/whatsapp-templates/:id/active',
      input: z.object({
        isActive: z.boolean(),
      }),
      responses: {
        200: whatsAppTemplateMutationResponseSchema,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
      },
    },
    linkMeta: {
      method: 'POST' as const,
      path: '/api/admin/whatsapp-templates/:id/link-meta',
      input: z.object({
        providerTemplateId: z.string().min(1).max(191),
      }),
      responses: {
        200: whatsAppTemplateMutationResponseSchema,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
      },
    },
    unlinkMeta: {
      method: 'POST' as const,
      path: '/api/admin/whatsapp-templates/:id/unlink-meta',
      responses: {
        200: whatsAppTemplateMutationResponseSchema,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
      },
    },
  },
  dashboard: {
    stats: {
      method: 'GET' as const,
      path: '/api/dashboard/stats',
      responses: {
        200: z.object({
          totalLeads: z.number(),
          conversionRate: z.number(),
          leadsByStatus: z.record(z.number()),
        }),
      },
    },
    upcomingFollowups: {
      method: 'GET' as const,
      path: '/api/dashboard/upcoming-followups',
      input: paginationQuerySchema.optional(),
      responses: {
        200: paginatedResponseSchema(leadListItemSchema),
      },
    },
  },
  places: {
    search: {
      method: 'GET' as const,
      path: '/api/places/search',
      input: z.object({ query: z.string() }),
      responses: {
        200: placeSearchResponseSchema,
      },
    },
    geocode: {
      method: 'GET' as const,
      path: '/api/places/geocode',
      input: z.object({ lat: z.string(), lng: z.string() }),
      responses: {
        200: z.object({
          address: z.string(),
        }),
      },
    },
    photo: {
      method: 'GET' as const,
      path: '/api/places/photo',
      input: z.object({ name: z.string() }),
      responses: {
        200: z.unknown(),
      },
    },
  },
  email: {
    send: {
      method: 'POST' as const,
      path: '/api/email/send',
      input: z.object({
        to: z.string().email(),
        subject: z.string(),
        body: z.string(),
        leadId: z.number(),
      }),
      responses: {
        200: z.object({
          success: z.boolean(),
          message: z.string(),
        }),
        400: errorSchemas.validation,
        500: errorSchemas.internal,
      },
    },
  },
  ai: {
    salesAssistant: {
      method: 'POST' as const,
      path: '/api/ai/sales-assistant/:leadId',
      input: z.object({
        locale: localeSchema.optional(),
        tone: aiToneSchema.optional(),
        forceRegenerate: z.boolean().optional(),
      }),
      responses: {
        200: aiSalesAssistantResponseSchema,
        401: errorSchemas.unauthorized,
        402: z.object({ message: z.string() }),
        403: z.object({ message: z.string() }),
        409: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
        500: errorSchemas.internal,
      },
    },
  },
  payment: {
    webhook: {
      method: 'POST' as const,
      path: '/api/razorpay/webhook',
      responses: {
        200: razorpayWebhookResponseSchema,
        403: z.object({ message: z.string() }),
        500: errorSchemas.internal,
      },
    },
    validateCoupon: {
      method: 'POST' as const,
      path: '/api/payment/validate-coupon',
      input: z.object({
        code: z.string().min(1),
        originalAmount: z.number().optional(),
      }),
      responses: {
        200: z.object({
          valid: z.boolean(),
          message: z.string().optional(),
          coupon: z
            .object({
              code: z.string(),
              discountType: z.enum(['percentage', 'fixed']),
              discountValue: z.number(),
              discountAmount: z.number(),
              discountPercent: z.number(),
              finalAmount: z.number(),
              bonusAmount: z.number(),
            })
            .optional(),
        }),
        400: errorSchemas.validation,
        401: errorSchemas.unauthorized,
        500: errorSchemas.internal,
      },
    },
    createOrder: {
      method: 'POST' as const,
      path: '/api/payment/create-order',
      input: z.object({
        isRenewal: z.boolean().optional(),
        couponCode: z.string().optional(),
      }),
      responses: {
        200: z.object({
          orderId: z.string(),
          amount: z.number(),
          currency: z.string(),
          key: z.string(),
        }),
        400: errorSchemas.validation,
        500: errorSchemas.internal,
      },
    },
    verifyPayment: {
      method: 'POST' as const,
      path: '/api/payment/verify',
      input: z.object({
        razorpay_order_id: z.string(),
        razorpay_payment_id: z.string(),
        razorpay_signature: z.string(),
        userId: z.number().optional(),
        couponCode: z.string().optional(),
      }),
      responses: {
        200: z.object({
          success: z.boolean(),
          message: z.string(),
          invoiceEmailQueued: z.boolean().optional(),
          walletBalance: z.number(),
          user: authUserSchema.optional(),
        }),
        400: errorSchemas.validation,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
        500: errorSchemas.internal,
      },
    },
    checkSubscription: {
      method: 'GET' as const,
      path: '/api/payment/check-subscription',
      responses: {
        200: z.object({
          isValid: z.boolean(),
          status: z.string(),
          expiryDate: z.string().optional(),
          daysRemaining: z.number().optional(),
        }),
        401: errorSchemas.unauthorized,
      },
    },
    subscriptionInvoice: {
      method: 'GET' as const,
      path: '/api/payment/subscription-invoice',
      input: z.object({ subscriptionId: z.coerce.number().int().positive().optional() }).optional(),
      responses: {
        200: subscriptionInvoiceResponseSchema,
        401: errorSchemas.unauthorized,
        404: errorSchemas.notFound,
        500: errorSchemas.internal,
      },
    },
    activateTrial: {
      method: 'POST' as const,
      path: '/api/payment/activate-trial',
      input: z.object({
        userId: z.number(),
      }),
      responses: {
        200: z.object({
          success: z.boolean(),
          message: z.string(),
          trialExpiryDate: z.string(),
        }),
        400: errorSchemas.validation,
        500: errorSchemas.internal,
      },
    },
  },
  wallet: {
    balance: {
      method: 'GET' as const,
      path: '/api/wallet/balance',
      responses: {
        200: walletBalanceResponseSchema,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
      },
    },
    transactions: {
      method: 'GET' as const,
      path: '/api/wallet/transactions',
      input: walletTransactionsQuerySchema.optional(),
      responses: {
        200: paginatedResponseSchema(walletTransactionItemSchema),
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
      },
    },
    createRechargeOrder: {
      method: 'POST' as const,
      path: '/api/wallet/recharge/create-order',
      input: walletRechargeCreateOrderSchema,
      responses: {
        200: walletRechargeOrderResponseSchema,
        400: errorSchemas.validation,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
        500: errorSchemas.internal,
      },
    },
    verifyRecharge: {
      method: 'POST' as const,
      path: '/api/wallet/recharge/verify',
      input: walletRechargeVerifySchema,
      responses: {
        200: walletRechargeVerifyResponseSchema,
        400: errorSchemas.validation,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
        500: errorSchemas.internal,
      },
    },
    creditReceipt: {
      method: 'GET' as const,
      path: '/api/wallet/credit-receipts/:id',
      input: z.object({ id: z.coerce.number().int().positive() }),
      responses: {
        200: walletCreditReceiptResponseSchema,
        400: errorSchemas.validation,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
        500: errorSchemas.internal,
      },
    },
  },
  broadcasts: {
    groups: {
      list: {
        method: 'GET' as const,
        path: '/api/broadcast-recipient-groups',
        responses: {
          200: z.array(broadcastRecipientGroupSummarySchema),
          401: errorSchemas.unauthorized,
          403: z.object({ message: z.string() }),
        },
      },
      create: {
        method: 'POST' as const,
        path: '/api/broadcast-recipient-groups',
        input: broadcastRecipientGroupUpsertSchema,
        responses: {
          201: broadcastRecipientGroupDetailSchema,
          400: errorSchemas.validation,
          401: errorSchemas.unauthorized,
          403: z.object({ message: z.string() }),
        },
      },
      createFromLeads: {
        method: 'POST' as const,
        path: '/api/broadcast-recipient-groups/from-leads',
        input: broadcastRecipientGroupFromLeadsSchema,
        responses: {
          201: broadcastRecipientGroupDetailSchema,
          400: errorSchemas.validation,
          401: errorSchemas.unauthorized,
          403: z.object({ message: z.string() }),
        },
      },
      get: {
        method: 'GET' as const,
        path: '/api/broadcast-recipient-groups/:id',
        responses: {
          200: broadcastRecipientGroupDetailSchema,
          401: errorSchemas.unauthorized,
          403: z.object({ message: z.string() }),
          404: errorSchemas.notFound,
        },
      },
      update: {
        method: 'PUT' as const,
        path: '/api/broadcast-recipient-groups/:id',
        input: broadcastRecipientGroupUpsertSchema,
        responses: {
          200: broadcastRecipientGroupDetailSchema,
          400: errorSchemas.validation,
          401: errorSchemas.unauthorized,
          403: z.object({ message: z.string() }),
          404: errorSchemas.notFound,
        },
      },
      addMember: {
        method: 'POST' as const,
        path: '/api/broadcast-recipient-groups/:id/members',
        input: broadcastRecipientGroupMemberInputSchema.omit({ id: true, leadId: true }),
        responses: {
          200: broadcastRecipientGroupDetailSchema,
          400: errorSchemas.validation,
          401: errorSchemas.unauthorized,
          403: z.object({ message: z.string() }),
          404: errorSchemas.notFound,
        },
      },
      delete: {
        method: 'DELETE' as const,
        path: '/api/broadcast-recipient-groups/:id',
        responses: {
          200: z.object({ success: z.boolean() }),
          401: errorSchemas.unauthorized,
          403: z.object({ message: z.string() }),
          404: errorSchemas.notFound,
        },
      },
    },
    preview: {
      method: 'POST' as const,
      path: '/api/broadcasts/preview',
      input: broadcastPreviewInputSchema,
      responses: {
        200: broadcastPreviewResponseSchema,
        400: errorSchemas.validation,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
      },
    },
    uploadMedia: {
      method: 'POST' as const,
      // Raw file body; metadata travels in the query string so no multipart parser is needed.
      path: '/api/broadcasts/media',
      input: broadcastMediaUploadQuerySchema,
      responses: {
        200: broadcastMediaUploadResponseSchema,
        400: errorSchemas.validation,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
        413: z.object({ message: z.string() }),
      },
    },
    create: {
      method: 'POST' as const,
      path: '/api/broadcasts',
      input: broadcastCreateInputSchema,
      responses: {
        201: broadcastCampaignSummarySchema,
        400: errorSchemas.validation,
        401: errorSchemas.unauthorized,
        402: z.object({ message: z.string() }),
        403: z.object({ message: z.string() }),
      },
    },
    list: {
      method: 'GET' as const,
      path: '/api/broadcasts',
      input: paginationQuerySchema
        .extend({
          search: z.string().optional(),
          status: broadcastCampaignStatusSchema.optional(),
        })
        .optional(),
      responses: {
        200: paginatedResponseSchema(broadcastCampaignSummarySchema),
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
      },
    },
    get: {
      method: 'GET' as const,
      path: '/api/broadcasts/:id',
      responses: {
        200: broadcastCampaignSummarySchema,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
      },
    },
    recipients: {
      method: 'GET' as const,
      path: '/api/broadcasts/:id/recipients',
      input: paginationQuerySchema.optional(),
      responses: {
        200: paginatedResponseSchema(broadcastRecipientItemSchema),
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
      },
    },
    retryFailed: {
      method: 'POST' as const,
      path: '/api/broadcasts/:id/retry-failed',
      responses: {
        200: z.object({
          retriedCount: z.number(),
          message: z.string(),
        }),
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
      },
    },
  },
  pwaShareTarget: {
    contacts: {
      path: PWA_CONTACT_SHARE_PATH,
      submit: {
        method: 'POST' as const,
        responses: {
          303: z.unknown(),
          401: errorSchemas.unauthorized,
        },
      },
      take: {
        method: 'GET' as const,
        responses: {
          200: z.object({
            contacts: z.array(
              z.object({
                name: z.string().nullable(),
                phoneNumber: z.string().nullable(),
                email: z.string().nullable(),
              })
            ),
          }),
          401: errorSchemas.unauthorized,
        },
      },
    },
  },
  conversations: {
    list: {
      method: 'GET' as const,
      path: '/api/conversations',
      input: paginationQuerySchema
        .extend({
          search: z.string().optional(),
          leadId: z.coerce.number().optional(),
          conversationId: z.coerce.number().int().positive().optional(),
          channel: conversationChannelSchema.optional(),
        })
        .optional(),
      responses: {
        200: paginatedResponseSchema(conversationListItemSchema).extend({
          totalUnreadCount: z.number(),
          unreadCountsByChannel: z.object({
            whatsapp: z.number(),
            facebook: z.number(),
            instagram: z.number(),
            threads: z.number(),
          }),
        }),
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
      },
    },
    messages: {
      method: 'GET' as const,
      path: '/api/conversations/:id/messages',
      input: paginationQuerySchema
        .extend({
          order: z.enum(['asc', 'desc']).optional(),
        })
        .optional(),
      responses: {
        200: paginatedResponseSchema(conversationMessageSchema),
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
      },
    },
    media: {
      method: 'GET' as const,
      path: '/api/conversations/:id/messages/:messageId/media',
      responses: {
        200: z.unknown(),
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
      },
    },
    markRead: {
      method: 'POST' as const,
      path: '/api/conversations/:id/read',
      responses: {
        200: conversationMarkReadResponseSchema,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
      },
    },
    presence: {
      method: 'POST' as const,
      path: '/api/conversations/:id/presence',
      input: conversationPresenceUpdateInputSchema,
      responses: {
        200: conversationPresenceUpdateResponseSchema,
        400: errorSchemas.validation,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
      },
    },
  },
  messages: {
    send: {
      method: 'POST' as const,
      path: '/api/messages/send',
      input: sendConversationMessageInputSchema,
      responses: {
        200: conversationMessageSchema,
        400: errorSchemas.validation,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
      },
    },
    createMediaUpload: {
      method: 'POST' as const,
      path: '/api/messages/media-upload',
      input: createConversationMediaUploadInputSchema,
      responses: {
        200: z.object({
          storageKey: z.string(),
          uploadUrl: z.string().url(),
          headers: z.record(z.string()),
          expiresAt: z.string(),
        }),
        400: errorSchemas.validation,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
        503: z.object({ message: z.string() }),
      },
    },
    sendMedia: {
      method: 'POST' as const,
      path: '/api/messages/send-media',
      input: sendConversationMediaInputSchema,
      responses: {
        200: conversationMessageSchema,
        400: errorSchemas.validation,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
        503: z.object({ message: z.string() }),
      },
    },
  },
  gmail: {
    connect: {
      method: 'POST' as const,
      path: '/api/gmail/connect',
      responses: {
        200: gmailConnectResponseSchema,
      },
    },
    callback: {
      method: 'GET' as const,
      path: '/api/gmail/callback',
      input: z.object({
        code: z.string().min(1),
        state: z.string().min(1),
      }),
    },
    account: {
      get: {
        method: 'GET' as const,
        path: '/api/gmail/account',
        responses: {
          200: gmailAccountSummarySchema.nullable(),
        },
      },
      unlink: {
        method: 'DELETE' as const,
        path: '/api/gmail/account',
        responses: {
          200: z.object({ success: z.literal(true) }),
        },
      },
      sync: {
        method: 'POST' as const,
        path: '/api/gmail/account/sync',
        responses: {
          200: z.object({
            processed: z.number().int().nonnegative(),
            candidateMessages: z.number().int().nonnegative(),
            examinedMessages: z.number().int().nonnegative(),
            leadsCreated: z.number().int().nonnegative(),
            existingLeadsUpdated: z.number().int().nonnegative(),
            ignoredMessages: z.number().int().nonnegative(),
            failedMessages: z.number().int().nonnegative(),
          }),
        },
      },
    },
    webhook: {
      method: 'POST' as const,
      path: '/api/gmail/webhook',
      input: gmailPubSubPushSchema,
      responses: {
        204: z.undefined(),
      },
    },
  },
  whatsapp: {
    flow: {
      method: 'POST' as const,
      path: '/api/whatsapp/flow',
      input: z.object({
        encrypted_flow_data: z.string().min(1).max(65536),
        encrypted_aes_key: z.string().min(1).max(2048),
        initial_vector: z.string().min(1).max(128),
      }),
      responses: { 200: z.string() },
    },
    connect: {
      method: 'POST' as const,
      path: '/api/whatsapp/connect',
      input: whatsappConnectInputSchema,
      responses: {
        200: whatsappConnectResponseSchema,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
      },
    },
    callback: {
      method: 'GET' as const,
      path: '/api/whatsapp/callback',
      input: whatsappCallbackQuerySchema,
      responses: {
        200: z.unknown(),
        400: errorSchemas.validation,
      },
    },
    embeddedSignup: {
      method: 'POST' as const,
      path: '/api/whatsapp/embedded-signup',
      input: whatsappEmbeddedSignupEventSchema,
      responses: {
        200: z.object({ success: z.boolean() }),
        400: errorSchemas.validation,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
      },
    },
    complete: {
      method: 'POST' as const,
      path: '/api/whatsapp/embedded-signup/complete',
      input: whatsappEmbeddedSignupCompleteSchema,
      responses: {
        200: whatsappEmbeddedSignupCompleteResponseSchema,
        400: errorSchemas.validation,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
      },
    },
    account: {
      list: {
        method: 'GET' as const,
        path: '/api/whatsapp/accounts',
        responses: {
          200: z.array(whatsappAccountSummarySchema),
          401: errorSchemas.unauthorized,
          403: z.object({ message: z.string() }),
        },
      },
      get: {
        method: 'GET' as const,
        path: '/api/whatsapp/account',
        responses: {
          200: whatsappAccountSummarySchema.nullable(),
          401: errorSchemas.unauthorized,
          403: z.object({ message: z.string() }),
        },
      },
      upsert: {
        method: 'POST' as const,
        path: '/api/whatsapp/account',
        input: whatsappAccountUpsertSchema,
        responses: {
          200: whatsappAccountSummarySchema,
          400: errorSchemas.validation,
          401: errorSchemas.unauthorized,
          403: z.object({ message: z.string() }),
        },
      },
      unlink: {
        method: 'POST' as const,
        path: '/api/whatsapp/account/:id/unlink',
        input: whatsappAccountUnlinkInputSchema,
        responses: {
          200: whatsappAccountUnlinkResponseSchema,
          400: errorSchemas.validation,
          401: errorSchemas.unauthorized,
          403: z.object({ message: z.string() }),
        },
      },
      capacity: {
        method: 'GET' as const,
        path: '/api/whatsapp/account/:id/capacity',
        responses: {
          200: whatsappAccountCapacitySchema,
          401: errorSchemas.unauthorized,
          403: z.object({ message: z.string() }),
          404: errorSchemas.notFound,
        },
      },
    },
    templates: {
      method: 'GET' as const,
      path: '/api/whatsapp/templates',
      input: whatsappAccountScopedQuerySchema,
      responses: {
        200: z.array(whatsappTemplateListItemSchema),
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
      },
    },
    templateSync: {
      method: 'POST' as const,
      path: '/api/whatsapp/templates/sync',
      input: whatsappAccountScopedQuerySchema,
      responses: {
        200: z.object({
          message: z.string(),
          syncedCount: z.number(),
          templates: z.array(whatsappTemplateListItemSchema),
        }),
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
      },
    },
    templateCreate: {
      method: 'POST' as const,
      path: '/api/whatsapp/templates',
      input: whatsAppTemplateDraftSchema
        .extend({
          whatsappAccountId: z.coerce.number().int().positive().optional(),
        })
        .transform(draft => ({
          ...draft,
          components:
            draft.metaCategory === 'AUTHENTICATION'
              ? draft.components
              : withBroadcastUnsubscribeFooter(draft.components),
        })),
      responses: {
        200: z.object({
          message: z.string(),
          template: whatsAppTemplateDetailSchema,
        }),
        400: errorSchemas.validation,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
      },
    },
    templateSampleMedia: {
      method: 'POST' as const,
      // Raw file body; uploads the sample to Meta's resumable upload API and returns the handle.
      path: '/api/whatsapp/templates/sample-media',
      input: whatsappTemplateSampleMediaQuerySchema,
      responses: {
        200: whatsAppTemplateSampleMediaSchema,
        400: errorSchemas.validation,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
        413: z.object({ message: z.string() }),
      },
    },
    templateManageList: {
      method: 'GET' as const,
      path: '/api/whatsapp/templates/manage',
      input: whatsappAccountScopedQuerySchema,
      responses: {
        200: z.array(whatsAppTemplateSummarySchema),
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
      },
    },
    templateManageGet: {
      method: 'GET' as const,
      path: '/api/whatsapp/templates/manage/:id',
      responses: {
        200: whatsAppTemplateDetailSchema,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
      },
    },
    templateManageUpdate: {
      method: 'PATCH' as const,
      path: '/api/whatsapp/templates/manage/:id',
      // Editable company drafts normalize the footer in the service using the stored category.
      input: whatsAppTemplateDraftSchema.partial(),
      responses: {
        200: z.object({
          message: z.string(),
          template: whatsAppTemplateDetailSchema,
        }),
        400: errorSchemas.validation,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
      },
    },
    templateManageDelete: {
      method: 'DELETE' as const,
      path: '/api/whatsapp/templates/manage/:id',
      input: z.object({
        scope: whatsAppTemplateDeleteScopeSchema,
      }),
      responses: {
        200: whatsAppTemplateDeleteResponseSchema,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
      },
    },
    templateManageSubmit: {
      method: 'POST' as const,
      path: '/api/whatsapp/templates/manage/:id/submit',
      input: whatsappAccountScopedQuerySchema,
      responses: {
        200: z.object({
          message: z.string(),
          template: whatsAppTemplateDetailSchema,
        }),
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
      },
    },
    templateManageRefresh: {
      method: 'POST' as const,
      path: '/api/whatsapp/templates/manage/:id/refresh',
      responses: {
        200: z.object({
          message: z.string(),
          template: whatsAppTemplateDetailSchema,
        }),
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
      },
    },
    templateManageActive: {
      method: 'PATCH' as const,
      path: '/api/whatsapp/templates/manage/:id/active',
      input: z.object({
        isActive: z.boolean(),
      }),
      responses: {
        200: z.object({
          message: z.string(),
          template: whatsAppTemplateDetailSchema,
        }),
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
      },
    },
    webhook: {
      method: 'POST' as const,
      path: '/api/whatsapp/webhook',
      responses: {
        200: z.object({ success: z.boolean() }),
        400: errorSchemas.validation,
        403: z.object({ message: z.string() }),
      },
    },
  },
  meta: {
    dataDeletion: {
      method: 'POST' as const,
      path: '/api/meta/data-deletion',
      input: metaSignedRequestInputSchema,
      responses: {
        200: metaDataDeletionResponseSchema,
        400: errorSchemas.validation,
        503: errorSchemas.internal,
      },
    },
    dataDeletionStatus: {
      method: 'GET' as const,
      path: '/api/meta/data-deletion/status',
      input: z
        .object({
          code: z.string().trim().min(1).optional(),
        })
        .optional(),
      responses: {
        200: metaDataDeletionStatusResponseSchema,
      },
    },
    deauthorize: {
      method: 'POST' as const,
      path: '/api/meta/deauthorize',
      input: metaSignedRequestInputSchema,
      responses: {
        200: metaDeauthorizeResponseSchema,
        400: errorSchemas.validation,
        503: errorSchemas.internal,
      },
    },
  },
  social: {
    assistantSettings: {
      method: 'GET' as const,
      path: '/api/social/assistant/settings',
      responses: { 200: socialAssistantSettingsSchema, 403: errorSchemas.unauthorized },
    },
    updateAssistantSettings: {
      method: 'PUT' as const,
      path: '/api/social/assistant/settings',
      input: socialAssistantSettingsSchema,
      responses: {
        200: socialAssistantSettingsSchema,
        400: errorSchemas.validation,
        403: errorSchemas.unauthorized,
      },
    },
    googleBusiness: {
      pendingConnection: {
        method: 'GET' as const,
        path: '/api/social/google_business/pending-connection',
        responses: {
          200: z.object({ locations: z.array(googleBusinessLocationSchema) }).nullable(),
          401: errorSchemas.unauthorized,
          403: z.object({ message: z.string() }),
        },
      },
      completeConnection: {
        method: 'POST' as const,
        path: '/api/social/google_business/pending-connection',
        input: z.object({ name: googleBusinessLocationSchema.shape.name }),
        responses: {
          200: z.object({ success: z.boolean() }),
          400: errorSchemas.validation,
          401: errorSchemas.unauthorized,
          403: z.object({ message: z.string() }),
          409: errorSchemas.internal,
          502: errorSchemas.internal,
          503: errorSchemas.internal,
        },
      },
      cancelConnection: {
        method: 'DELETE' as const,
        path: '/api/social/google_business/pending-connection',
        responses: {
          200: z.object({ success: z.boolean() }),
          401: errorSchemas.unauthorized,
          403: z.object({ message: z.string() }),
        },
      },
      locations: {
        method: 'GET' as const,
        path: '/api/social/google_business/accounts/:id/locations',
        responses: {
          200: z.array(googleBusinessLocationSchema),
          400: errorSchemas.validation,
          401: errorSchemas.unauthorized,
          403: z.object({ message: z.string() }),
          404: errorSchemas.notFound,
          502: errorSchemas.internal,
          429: errorSchemas.internal,
          503: errorSchemas.internal,
        },
      },
      selectLocation: {
        method: 'PUT' as const,
        path: '/api/social/google_business/accounts/:id/location',
        input: z.object({ name: googleBusinessLocationSchema.shape.name }),
        responses: {
          200: z.object({ success: z.boolean() }),
          400: errorSchemas.validation,
          401: errorSchemas.unauthorized,
          403: z.object({ message: z.string() }),
          404: errorSchemas.notFound,
          502: errorSchemas.internal,
          429: errorSchemas.internal,
          503: errorSchemas.internal,
        },
      },
      reviews: {
        method: 'GET' as const,
        path: '/api/social/google_business/accounts/:id/reviews',
        input: z.object({ cursor: z.string().max(4096).optional() }),
        responses: {
          200: googleBusinessReviewsSchema,
          400: errorSchemas.validation,
          401: errorSchemas.unauthorized,
          403: z.object({ message: z.string() }),
          404: errorSchemas.notFound,
          502: errorSchemas.internal,
          429: errorSchemas.internal,
          503: errorSchemas.internal,
        },
      },
      reply: {
        method: 'PUT' as const,
        path: '/api/social/google_business/accounts/:id/reviews/:reviewId/reply',
        input: z.object({
          locationName: googleBusinessLocationSchema.shape.name,
          comment: z
            .string()
            .trim()
            .min(1)
            .max(GOOGLE_BUSINESS_REPLY_MAX_BYTES)
            .refine(
              value => new TextEncoder().encode(value).length <= GOOGLE_BUSINESS_REPLY_MAX_BYTES,
              'Reply must not exceed 4096 UTF-8 bytes'
            ),
        }),
        responses: {
          200: googleBusinessReviewReplySchema,
          400: errorSchemas.validation,
          401: errorSchemas.unauthorized,
          403: z.object({ message: z.string() }),
          404: errorSchemas.notFound,
          409: errorSchemas.internal,
          502: errorSchemas.internal,
          429: errorSchemas.internal,
          503: errorSchemas.internal,
        },
      },
    },
    accounts: {
      method: 'GET' as const,
      path: '/api/social/accounts',
      input: z
        .object({
          platform: z
            .enum(['facebook', 'instagram', 'threads', 'google_business', 'youtube'])
            .optional(),
        })
        .optional(),
      responses: {
        200: z.array(socialAccountSummarySchema),
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
      },
    },
    connect: {
      method: 'POST' as const,
      path: '/api/social/:platform/connect',
      responses: {
        200: z.object({ authorizationUrl: z.string().url() }),
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
        503: errorSchemas.internal,
      },
    },
    callback: {
      method: 'GET' as const,
      path: '/api/social/:platform/callback',
      responses: { 302: z.unknown() },
    },
    disconnect: {
      method: 'DELETE' as const,
      path: '/api/social/accounts/:id',
      responses: {
        200: z.object({ success: z.boolean() }),
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
      },
    },
    publishPost: {
      method: 'POST' as const,
      path: '/api/social/posts',
      input: socialPostPublishInputSchema,
      responses: {
        200: socialPostPublishResponseSchema,
        400: errorSchemas.validation,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
        409: errorSchemas.internal,
        503: z.object({ message: z.string() }),
      },
    },
    listPosts: {
      method: 'GET' as const,
      path: '/api/social/:platform/posts',
      input: socialPostsListInputSchema,
      responses: {
        200: socialPostsListResponseSchema,
        400: errorSchemas.validation,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
        429: errorSchemas.internal,
        502: apiErrorResponseSchema,
        503: errorSchemas.internal,
      },
    },
    deletePost: {
      method: 'DELETE' as const,
      path: '/api/social/:platform/posts/:postId',
      responses: {
        200: z.object({ success: z.boolean() }),
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
        422: z.object({ message: z.string() }),
        503: errorSchemas.internal,
      },
    },
    automation: {
      createManualVideo: {
        method: 'POST' as const,
        path: '/api/social/automation/manual-video',
        input: aiSocialManualVideoInputSchema,
        responses: {
          202: aiSocialPostSummarySchema,
          400: errorSchemas.validation,
          401: errorSchemas.unauthorized,
          403: z.object({ message: z.string() }),
          402: z.object({ message: z.string() }),
          503: errorSchemas.internal,
        },
      },
      list: {
        method: 'GET' as const,
        path: '/api/social/automation/posts',
        input: aiSocialPostsListInputSchema.optional(),
        responses: {
          200: aiSocialPostsListResponseSchema,
          401: errorSchemas.unauthorized,
          403: z.object({ message: z.string() }),
        },
      },
      get: {
        method: 'GET' as const,
        path: '/api/social/automation/posts/:id',
        responses: {
          200: aiSocialPostSummarySchema,
          401: errorSchemas.unauthorized,
          403: z.object({ message: z.string() }),
          404: errorSchemas.notFound,
        },
      },
      updateCaption: {
        method: 'PATCH' as const,
        path: '/api/social/automation/posts/:id',
        input: aiSocialPostCaptionInputSchema,
        responses: {
          200: aiSocialPostSummarySchema,
          400: errorSchemas.validation,
          401: errorSchemas.unauthorized,
          403: z.object({ message: z.string() }),
          404: errorSchemas.notFound,
        },
      },
      approve: {
        method: 'POST' as const,
        path: '/api/social/automation/posts/:id/approve',
        responses: {
          202: aiSocialPostSummarySchema,
          400: errorSchemas.validation,
          401: errorSchemas.unauthorized,
          403: z.object({ message: z.string() }),
          404: errorSchemas.notFound,
        },
      },
      reject: {
        method: 'POST' as const,
        path: '/api/social/automation/posts/:id/reject',
        responses: {
          200: aiSocialPostSummarySchema,
          400: errorSchemas.validation,
          401: errorSchemas.unauthorized,
          403: z.object({ message: z.string() }),
          404: errorSchemas.notFound,
        },
      },
      retryPublish: {
        method: 'POST' as const,
        path: '/api/social/automation/posts/:id/retry-publish',
        responses: {
          202: aiSocialPostSummarySchema,
          400: errorSchemas.validation,
          401: errorSchemas.unauthorized,
          403: z.object({ message: z.string() }),
          404: errorSchemas.notFound,
        },
      },
    },
    manageThreadsReply: {
      method: 'POST' as const,
      path: '/api/social/threads/replies/:replyId/manage',
      input: manageThreadsReplyInputSchema,
      responses: {
        200: z.object({ success: z.boolean() }),
        400: errorSchemas.validation,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
        422: z.object({ message: z.string() }),
        503: errorSchemas.internal,
      },
    },
    webhook: {
      method: 'POST' as const,
      path: '/api/social/webhook',
      responses: {
        200: z.object({ success: z.boolean() }),
        403: z.object({ message: z.string() }),
      },
    },
  },
  admin: {
    getStats: {
      method: 'GET' as const,
      path: '/api/admin/stats',
      responses: {
        200: z.object({
          totalUsers: z.number(),
          activeSubscribers: z.number(),
          expiringSoon: z.number(),
          totalRevenue: z.number(),
        }),
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
      },
    },
    getSubscriberStats: {
      method: 'GET' as const,
      path: '/api/admin/subscriber-stats',
      responses: {
        200: superAdminSubscriberStatsResponseSchema,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
      },
    },
    getUsers: {
      method: 'GET' as const,
      path: '/api/admin/users',
      responses: {
        200: z.array(superAdminUserListItemSchema),
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
      },
    },
    listServicePricing: {
      method: 'GET' as const,
      path: '/api/admin/service-pricing',
      responses: {
        200: z.array(servicePricingItemSchema),
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
      },
    },
    updateServicePricing: {
      method: 'PUT' as const,
      path: '/api/admin/service-pricing/:serviceKey',
      input: servicePricingUpdateSchema,
      responses: {
        200: servicePricingItemSchema,
        400: errorSchemas.validation,
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
      },
    },
    coupons: {
      list: {
        method: 'GET' as const,
        path: '/api/admin/coupons',
        responses: {
          200: z.array(couponSchema),
          401: errorSchemas.unauthorized,
          403: z.object({ message: z.string() }),
        },
      },
      create: {
        method: 'POST' as const,
        path: '/api/admin/coupons',
        input: z.object({
          code: z.string().min(1),
          discountType: z.enum(['percentage', 'fixed']),
          discountValue: z.number().positive(),
          maxUses: z.number().optional().nullable(),
          minOrderAmount: z.number().optional(),
          expiresAt: z.string().optional().nullable(),
          isActive: z.boolean().optional(),
        }),
        responses: {
          200: couponSchema,
          400: errorSchemas.validation,
          401: errorSchemas.unauthorized,
          403: z.object({ message: z.string() }),
        },
      },
      update: {
        method: 'PATCH' as const,
        path: '/api/admin/coupons/:id',
        input: z.object({
          code: z.string().optional(),
          discountType: z.enum(['percentage', 'fixed']).optional(),
          discountValue: z.number().positive().optional(),
          maxUses: z.number().optional().nullable(),
          minOrderAmount: z.number().optional(),
          expiresAt: z.string().optional().nullable(),
          isActive: z.boolean().optional(),
        }),
        responses: {
          200: couponSchema,
          400: errorSchemas.validation,
          401: errorSchemas.unauthorized,
          403: z.object({ message: z.string() }),
        },
      },
      delete: {
        method: 'DELETE' as const,
        path: '/api/admin/coupons/:id',
        responses: {
          200: z.object({ success: z.boolean() }),
          401: errorSchemas.unauthorized,
          403: z.object({ message: z.string() }),
        },
      },
    },
  },
  teams: {
    getTeamMembers: {
      method: 'GET' as const,
      path: '/api/manager/team-members',
      responses: {
        200: z.array(teamMemberSchema),
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
      },
    },
  },
  contact: {
    submit: {
      method: 'POST' as const,
      path: '/api/contact',
      input: insertContactMessageSchema,
      responses: {
        201: contactMessageSchema,
        400: errorSchemas.validation,
      },
    },
    list: {
      method: 'GET' as const,
      path: '/api/contact/messages',
      responses: {
        200: z.array(contactMessageSchema),
      },
    },
    updateStatus: {
      method: 'PATCH' as const,
      path: '/api/contact/messages/:id',
      input: z.object({
        status: z.enum(['new', 'in_progress', 'resolved']),
      }),
      responses: {
        200: contactMessageSchema,
        404: errorSchemas.notFound,
      },
    },
  },
  debug: {
    notificationQueue: {
      method: 'GET' as const,
      path: '/api/debug/notifications/queue',
      input: z
        .object({
          limit: z.coerce.number().optional(),
        })
        .optional(),
      responses: {
        200: z.array(notificationQueueDebugItemSchema),
        401: errorSchemas.unauthorized,
        403: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
      },
    },
  },
};

export function buildUrl(path: string, params?: Record<string, string | number>): string {
  let url = path;
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (url.includes(`:${key}`)) {
        url = url.replace(`:${key}`, String(value));
      }
    });
  }
  return url;
}
